import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send, History, RefreshCw, TrendingUp, TrendingDown, Minus, Award } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, dt, SectionCard, SettingsHero, StickyActionsBar } from "../components/common";
import { Modal, Field, TextArea, Selecter } from "./crm/crmui";
import WysiwygEditor from "../components/WysiwygEditor";
import { RichText } from "../components/RichText";
import PlatformPicker from "../components/PlatformPicker";
import EmailMarketingTab from "./settings/EmailMarketingTab";

const TABS = [
  { key: "email", label: "Email (SendGrid)" },
  { key: "email_marketing", label: "Email Marketing" },
  { key: "canais", label: "Canais" },
  { key: "templates", label: "Templates" },
  { key: "assinatura_digital", label: "Assinatura Digital" },
  { key: "kb", label: "Base de Conhecimento" },
  { key: "plataformas", label: "Plataformas" },
  { key: "produtos", label: "Produtos" },
  { key: "cpq_workflow", label: "Workflow CPQ" },
  { key: "cpq_renewal", label: "Renovacao Contratos" },
  { key: "cpq_templates", label: "Templates Propostas" },
  { key: "cpq_cover_letters", label: "Cartas Apresentacao" },
  { key: "task_prefs", label: "Alertas de Prazo" },
  { key: "funis", label: "Funis" },
  { key: "financeiro", label: "Financeiro" },
  { key: "metas", label: "Metas" },
  { key: "leads", label: "Leads" },
  { key: "shortlinks", label: "Shortlinks" },
  { key: "suporte_tecnico", label: "Suporte Tecnico" },
  { key: "calendario", label: "Calendario (O365)" },
  { key: "manutencao", label: "Manutencao" },
  { key: "backups", label: "Backups" },
  { key: "resumo_operacional", label: "Resumo Operacional" },
  { key: "documentacao", label: "Documentacao" },
];

export default function Settings() {
  const [tab, setTab] = useState("email");
  return (
    <>
      <PageHeader overline="Definicoes" title="Definicoes da plataforma" />
      <div className="flex flex-col lg:flex-row gap-6" data-testid="settings-layout">
        <aside className="lg:w-64 shrink-0" data-testid="settings-sidebar">
          <div className="bg-white border border-tint rounded-lg p-2 sticky top-4 max-h-[calc(100vh-40px)] overflow-y-auto">
            <div className="text-[10px] uppercase tracking-wider text-muted2 font-semibold px-2 pt-1 pb-2">Categorias</div>
            <nav className="flex flex-col gap-0.5">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
                  className={`px-3 py-2 text-sm text-left rounded-md transition-colors ${tab === t.key ? "bg-blue text-white font-semibold" : "text-navy hover:bg-tint"}`}>
                  {t.label}
                </button>
              ))}
            </nav>
          </div>
        </aside>
        <main className="flex-1 min-w-0" data-testid="settings-content">
      {tab === "email" && <EmailTab />}
      {tab === "email_marketing" && <EmailMarketingTab />}
      {tab === "canais" && <CanaisTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "assinatura_digital" && <AssinaturaDigitalTab />}
      {tab === "kb" && <KBTab />}
      {tab === "plataformas" && <PlataformasTab />}
      {tab === "produtos" && <ProdutosTab />}
      {tab === "cpq_workflow" && <CpqWorkflowTab />}
      {tab === "cpq_renewal" && <CpqRenewalTab />}
      {tab === "cpq_templates" && <CpqTemplatesTab />}
      {tab === "cpq_cover_letters" && <CoverLettersTab />}
      {tab === "task_prefs" && <TaskDeadlinePrefsTab />}
      {tab === "funis" && <FunisTab />}
      {tab === "financeiro" && <FinanceiroTab />}
      {tab === "metas" && <MetasTab />}
      {tab === "leads" && <LeadsTab />}
      {tab === "shortlinks" && <ShortlinksTab />}
      {tab === "manutencao" && <ManutencaoTab />}
      {tab === "backups" && <BackupsTab />}
      {tab === "resumo_operacional" && <ResumoOperacionalTab />}
      {tab === "documentacao" && <DocumentacaoTab />}
      {tab === "suporte_tecnico" && <SuporteTecnicoTab />}
      {tab === "calendario" && <CalendarTab />}
        </main>
      </div>
    </>
  );
}

function SubTabs({ tabs, current, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1 mb-4 border-b border-tint" data-testid={testid}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          data-testid={`${testid}-${t.key}`}
          className={`px-3 py-1.5 text-[13px] font-medium border-b-2 -mb-px transition-colors ${current === t.key ? "border-blue text-blue" : "border-transparent text-muted2 hover:text-navy"}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function FinanceiroTab() {
  const [sub, setSub] = useState("moloni");
  return (
    <div data-testid="financeiro-tab">
      <SubTabs
        tabs={[
          { key: "moloni", label: "Faturacao (Moloni)" },
          { key: "eupago", label: "Gateway Pagamentos (EuPago)" },
          { key: "credito", label: "Controle de Credito" },
        ]}
        current={sub}
        onChange={setSub}
        testid="financeiro-subtabs"
      />
      {sub === "moloni" && <MoloniTab />}
      {sub === "eupago" && <EuPagoTab />}
      {sub === "credito" && <ControleCreditoTab />}
    </div>
  );
}


function ControleCreditoTab() {
  const [sub, setSub] = useState("credito");
  return (
    <div data-testid="controle-credito">
      <SubTabs
        tabs={[
          { key: "credito", label: "Limite de credito" },
          { key: "dunning", label: "Dunning (cobrancas)" },
        ]}
        current={sub}
        onChange={setSub}
        testid="subtabs-credito"
      />
      {sub === "credito" && <CreditTab />}
      {sub === "dunning" && <DunningTab />}
    </div>
  );
}

function SuporteTecnicoTab() {
  const [sub, setSub] = useState("sla");
  return (
    <div data-testid="suporte-tecnico">
      <SubTabs
        tabs={[
          { key: "sla", label: "SLA" },
          { key: "regras_tickets", label: "Regras de tickets" },
        ]}
        current={sub}
        onChange={setSub}
        testid="subtabs-suporte"
      />
      {sub === "sla" && <SLATab />}
      {sub === "regras_tickets" && <RegrasTicketsTab />}
    </div>
  );
}

function CalendarTab() {
  const [s, setS] = useState({ configured: false, organizer_user: "", client_id_masked: "", tenant_masked: "" });
  const [cred, setCred] = useState({ tenant_id: "", client_id: "", client_secret: "" });

  const load = async () => { try { const { data } = await api.get("/scheduling/settings"); setS(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try {
      await api.put("/scheduling/settings", {
        tenant_id: cred.tenant_id || undefined, client_id: cred.client_id || undefined,
        client_secret: cred.client_secret || undefined, organizer_user: s.organizer_user,
      });
      toast.success("Definicoes guardadas."); setCred({ tenant_id: "", client_id: "", client_secret: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const testar = async () => { try { const { data } = await api.post("/scheduling/test"); toast.success(`Ligacao O365 OK (${data.calendar}).`); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="max-w-3xl space-y-4" data-testid="calendar-settings">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-navy">Estado:</span>
        <Badge value={s.configured ? "ativo" : "nao_configurado"} />
        {s.client_id_masked && <span className="text-xs text-muted2">client_id: {s.client_id_masked}</span>}
      </div>
      <p className="text-xs text-muted2">Registe uma aplicacao no Microsoft Entra (Azure AD) com a permissao de aplicacao Calendars.ReadWrite (consentimento de admin). As credenciais sao guardadas cifradas. Deixe vazio para manter.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Tenant ID (Directory ID)" type="password" value={cred.tenant_id} onChange={(v) => setCred({ ...cred, tenant_id: v })} testid="m365-tenant" />
        <Field label="Client ID (Application ID)" type="password" value={cred.client_id} onChange={(v) => setCred({ ...cred, client_id: v })} testid="m365-client" />
        <Field label="Client Secret" type="password" value={cred.client_secret} onChange={(v) => setCred({ ...cred, client_secret: v })} testid="m365-secret" />
        <Field label="Mailbox do organizador (email)" value={s.organizer_user} onChange={(v) => setS({ ...s, organizer_user: v })} placeholder="agenda@cogniseg.net" testid="m365-organizer" />
      </div>
      <div className="flex gap-2"><Btn onClick={save} testid="m365-save">Guardar</Btn><Btn variant="ghost" onClick={testar} testid="m365-test">Testar ligacao</Btn></div>
    </div>
  );
}

function DunningTab() {
  const [s, setS] = useState({ max_tentativas: 3, schedule_days: [3, 7, 14] });
  const [dias, setDias] = useState("3, 7, 14");

  const load = async () => {
    try {
      const { data } = await api.get("/dunning/settings");
      setS(data); setDias((data.schedule_days || []).join(", "));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    const schedule_days = dias.split(",").map((x) => parseInt(x.trim())).filter((n) => !isNaN(n) && n > 0);
    try {
      await api.put("/dunning/settings", { max_tentativas: parseInt(s.max_tentativas) || 3, schedule_days });
      toast.success("Definicoes de dunning guardadas."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl space-y-4" data-testid="dunning-settings">
      <p className="text-xs text-muted2">Configure o processo de cobranca de faturas vencidas. Sao enviados lembretes por email nos intervalos indicados; esgotadas as tentativas, a subscricao e suspensa (propaga para a CogniEtica).</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Numero maximo de lembretes" type="number" value={s.max_tentativas} onChange={(v) => setS({ ...s, max_tentativas: v })} testid="dn-max" />
        <Field label="Dias entre lembretes (separados por virgula)" value={dias} onChange={setDias} placeholder="3, 7, 14" testid="dn-days" />
      </div>
      <div className="text-xs text-muted2 bg-tint rounded-lg p-3">
        Atual: <b>{s.max_tentativas}</b> lembretes; escalonamento a <b>{(s.schedule_days || []).join(", ")}</b> dias. Apos o ultimo lembrete sem pagamento, a instancia e suspensa.
      </div>
      <Btn onClick={save} testid="dn-save">Guardar</Btn>
    </div>
  );
}

function EuPagoTab() {
  const [s, setS] = useState({ configured: false, live: false, webhook_secret_set: false, api_key_masked: "" });
  const [apiKey, setApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => { try { const { data } = await api.get("/eupago/settings"); setS(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try {
      await api.put("/eupago/settings", { api_key: apiKey || undefined, webhook_secret: webhookSecret || undefined, live: s.live });
      toast.success("Definicoes guardadas."); setApiKey(""); setWebhookSecret(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const testar = async () => { try { const { data } = await api.post("/eupago/test"); toast[data.ok ? "success" : "message"](data.ok ? "Ligacao EuPago OK (referencia de teste criada)." : "Resposta EuPago recebida, verifique a conta."); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="max-w-3xl space-y-4" data-testid="eupago-settings">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-navy">Estado:</span>
        <Badge value={s.configured ? "ativo" : "nao_configurado"} />
        {s.api_key_masked && <span className="text-xs text-muted2">ApiKey: {s.api_key_masked}</span>}
        <Badge value={s.live ? "producao" : "sandbox"} />
      </div>
      <p className="text-xs text-muted2">A ApiKey EuPago (por canal) e guardada cifrada. Ative Multibanco, MB WAY e Cartao no canal EuPago. Deixe vazio para manter.</p>
      <Field label="ApiKey EuPago (deixe vazio para manter)" type="password" value={apiKey} onChange={setApiKey} placeholder="demo-xxxx" testid="eu-api-key" />
      <Field label="Segredo do Webhook (assinatura X-Signature, opcional)" type="password" value={webhookSecret} onChange={setWebhookSecret} placeholder="deixe vazio para manter" testid="eu-webhook-secret" />
      <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={s.live} onChange={(e) => setS({ ...s, live: e.target.checked })} data-testid="eu-live" /> Modo producao (desligado = Sandbox)</label>
      <div className="text-xs text-muted2 bg-tint rounded-lg p-3">
        URL do webhook a configurar no canal EuPago (Realtime Webhooks 2.0, POST):<br />
        <code className="break-all">{backend}/api/webhooks/eupago</code>
      </div>
      <div className="flex gap-2"><Btn onClick={save} testid="eu-save">Guardar</Btn><Btn variant="ghost" onClick={testar} testid="eu-test">Testar ligacao</Btn></div>
    </div>
  );
}

function MoloniTab() {
  const [s, setS] = useState({ configured: false, company_id: "", document_set_id: "", tax_id: "", payment_method_id: "", auto_emitir: false, client_id_masked: "" });
  const [cred, setCred] = useState({ client_id: "", client_secret: "", username: "", password: "" });

  const load = async () => { try { const { data } = await api.get("/moloni/settings"); setS({ ...data, company_id: data.company_id || "", document_set_id: data.document_set_id || "", tax_id: data.tax_id || "", payment_method_id: data.payment_method_id || "" }); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try {
      await api.put("/moloni/settings", {
        client_id: cred.client_id || undefined, client_secret: cred.client_secret || undefined,
        username: cred.username || undefined, password: cred.password || undefined,
        company_id: s.company_id ? parseInt(s.company_id) : undefined,
        document_set_id: s.document_set_id ? parseInt(s.document_set_id) : undefined,
        tax_id: s.tax_id ? parseInt(s.tax_id) : undefined,
        payment_method_id: s.payment_method_id ? parseInt(s.payment_method_id) : undefined,
        auto_emitir: s.auto_emitir,
      });
      toast.success("Definicoes guardadas."); setCred({ client_id: "", client_secret: "", username: "", password: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const testar = async () => { try { const { data } = await api.post("/moloni/test"); toast.success("Ligacao Moloni OK."); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="max-w-3xl space-y-4" data-testid="moloni-settings">
      <div className="flex items-center gap-2"><span className="text-sm font-semibold text-navy">Estado:</span><Badge value={s.configured ? "ativo" : "nao_configurado"} />{s.client_id_masked && <span className="text-xs text-muted2">client_id: {s.client_id_masked}</span>}</div>
      <p className="text-xs text-muted2">Credenciais OAuth2 Moloni (guardadas cifradas). Obtenha em moloni.pt (Aplicacoes/API). Deixe vazio para manter.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Client ID" type="password" value={cred.client_id} onChange={(v) => setCred({ ...cred, client_id: v })} testid="ml-client-id" />
        <Field label="Client Secret" type="password" value={cred.client_secret} onChange={(v) => setCred({ ...cred, client_secret: v })} testid="ml-client-secret" />
        <Field label="Utilizador (email)" value={cred.username} onChange={(v) => setCred({ ...cred, username: v })} testid="ml-username" />
        <Field label="Password" type="password" value={cred.password} onChange={(v) => setCred({ ...cred, password: v })} testid="ml-password" />
      </div>
      <div className="grid md:grid-cols-4 gap-3">
        <Field label="Company ID" value={s.company_id} onChange={(v) => setS({ ...s, company_id: v })} testid="ml-company" />
        <Field label="Serie (document_set_id)" value={s.document_set_id} onChange={(v) => setS({ ...s, document_set_id: v })} testid="ml-docset" />
        <Field label="Imposto (tax_id)" value={s.tax_id} onChange={(v) => setS({ ...s, tax_id: v })} testid="ml-tax" />
        <Field label="Metodo pagto." value={s.payment_method_id} onChange={(v) => setS({ ...s, payment_method_id: v })} testid="ml-payment" />
      </div>
      <label className="flex items-center gap-2 text-sm text-navy"><input type="checkbox" checked={s.auto_emitir} onChange={(e) => setS({ ...s, auto_emitir: e.target.checked })} data-testid="ml-auto" /> Emitir fatura automaticamente ao ativar contrato</label>
      <div className="flex gap-2"><Btn onClick={save} testid="ml-save">Guardar</Btn><Btn variant="ghost" onClick={testar} testid="ml-test">Testar ligacao</Btn></div>
    </div>
  );
}

function CanaisTab() {
  return (
    <div className="space-y-5" data-testid="canais-settings">
      <SettingsHero
        iconLabel="CH"
        title="Canais de comunicacao"
        description="Configuracao dos canais externos usados pelo Hub: SMS (SMS Buzz), telefone (Let's Call), chat do site (widget publico) e Telegram (alertas + handoffs). Cada seccao guarda-se independentemente."
      />

      <SectionCard title="SMS (SMS Buzz)" hint="Envio de mensagens SMS transacionais e comerciais.">
        <SmsTab />
      </SectionCard>

      <SectionCard title="Telefone (Let's Call)" hint="Integracao com o dialer Let's Call para chamadas web.">
        <LetsCallTab />
      </SectionCard>

      <SectionCard title="Chat do site (widget)" hint="Snippet de embed e configuracao do bot AI apresentado ao publico.">
        <ChatWidgetTab />
      </SectionCard>

      <SectionCard title="Telegram (Alertas + Handoffs)" hint="Ponto central: bot Telegram usado por todas as fontes de alerta do Hub.">
        <TelegramConvergenceTab />
      </SectionCard>
    </div>
  );
}

// Fontes de alerta que reutilizam o mesmo bot Telegram (informativo + botao de teste).
// O envio real e feito pelos modulos backend (chat_ai, security, connector_health, ...);
// o botao "Testar" aqui envia uma mensagem etiquetada com a fonte, para o operador validar
// visualmente que a fonte esta a chegar ao chat correcto.
const TG_ALERT_SOURCES = [
  { key: "seguranca",   label: "Seguranca (login, pais, allowlist)", desc: "Login spikes, tentativas de acesso, anomalias por pais." },
  { key: "handoffs",    label: "Handoffs / SLA",                     desc: "Chat, formularios e emails que escalam para humano; alerta SLA a cada 5 min." },
  { key: "conectores",  label: "Saude de Conectores (API down)",     desc: "Ping automatico aos conectores externos (Privaxia, CogniEtica, etc)." },
  { key: "drift",       label: "Subscription Drift",                 desc: "Divergencia entre Hub e conector durante sync." },
  { key: "orfaos",      label: "Orfaos do Portal (30d)",             desc: "Subscricoes por reclamar apos 30 dias." },
];

function TelegramConvergenceTab() {
  const [tg, setTg] = useState({ configured: false, enabled: false, bot_username: null, chat_id_hint: null });
  const [tgForm, setTgForm] = useState({ bot_token: "", chat_id: "", enabled: true });
  const [sla, setSla] = useState({ supervisor_email: "", supervisor_telegram_chat_id: "" });
  const [saving, setSaving] = useState(false);
  const [savingSup, setSavingSup] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingSource, setTestingSource] = useState("");
  const [canManage, setCanManage] = useState(true);

  const loadAll = async () => {
    try {
      let tgRes = null;
      try {
        tgRes = await api.get("/security/telegram");
        setCanManage(true);
      } catch {
        tgRes = await api.get("/security/telegram/status");
        setCanManage(false);
      }
      const slaRes = await api.get("/chat-ai/handoffs/sla-config").catch(() => ({ data: {} }));
      if (tgRes?.data) {
        setTg(tgRes.data);
        setTgForm((f) => ({ ...f, enabled: tgRes.data.enabled ?? true }));
      }
      if (slaRes?.data) setSla({
        supervisor_email: slaRes.data.supervisor_email || "",
        supervisor_telegram_chat_id: slaRes.data.supervisor_telegram_chat_id || "",
        default_comercial_h: slaRes.data.default_comercial_h ?? 2,
        default_tecnico_h: slaRes.data.default_tecnico_h ?? 1,
        per_platform: slaRes.data.per_platform || {},
        auto_assign: slaRes.data.auto_assign ?? true,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const saveTelegram = async (asTest) => {
    if (!tgForm.bot_token || !tgForm.chat_id) {
      toast.error("Indique bot token e chat ID."); return;
    }
    setSaving(true);
    try {
      if (asTest) {
        const { data } = await api.post("/security/telegram/test", tgForm);
        if (data?.ok) toast.success(`Teste OK${data.bot_username ? " - @" + data.bot_username : ""}.`);
        else toast.error("Teste falhou.");
      } else {
        const { data } = await api.put("/security/telegram", tgForm);
        toast.success(`Guardado${data?.bot_username ? " - @" + data.bot_username : ""}.`);
        setTgForm({ bot_token: "", chat_id: "", enabled: tgForm.enabled });
        loadAll();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao guardar/testar Telegram.");
    } finally { setSaving(false); }
  };

  const testarBot = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/chat-ai/handoffs/notify-test");
      if (data?.ok) toast.success("Mensagem de teste enviada por Telegram.");
      else toast.error("Telegram nao configurado ou envio falhou.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao testar.");
    } finally { setTesting(false); }
  };

  const testarFonte = async (fonte) => {
    setTestingSource(fonte.key);
    try {
      const { data } = await api.post("/security/telegram/source-test", { source: fonte.key, label: fonte.label });
      if (data?.ok) toast.success(`Alerta simulado enviado (${fonte.label}).`);
      else toast.error(data?.reason || "Falha ao enviar alerta simulado.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao enviar alerta simulado.");
    } finally { setTestingSource(""); }
  };

  const saveSupervisor = async () => {
    setSavingSup(true);
    try {
      await api.put("/chat-ai/handoffs/sla-config", {
        default_comercial_h: Number(sla.default_comercial_h ?? 2),
        default_tecnico_h: Number(sla.default_tecnico_h ?? 1),
        per_platform: sla.per_platform || {},
        auto_assign: !!sla.auto_assign,
        supervisor_email: (sla.supervisor_email || "").trim(),
        supervisor_telegram_chat_id: (sla.supervisor_telegram_chat_id || "").trim(),
      });
      toast.success("Supervisor SLA guardado.");
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingSup(false); }
  };

  const status = tg.configured && tg.enabled
    ? { txt: "Ativo", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" }
    : tg.configured
      ? { txt: "Configurado mas desligado", cls: "text-amber-700 bg-amber-50 border-amber-200" }
      : { txt: "Nao configurado", cls: "text-red-700 bg-red-50 border-red-200" };

  return (
    <div className="max-w-3xl space-y-6" data-testid="telegram-convergence">
      <p className="text-xs text-muted2">
        Ponto central de configuracao e monitorizacao do Telegram. O bot aqui definido e reutilizado por todos os
        alertas do Hub (seguranca, handoffs, conectores, drift de subscricoes e orfaos do portal).
      </p>

      {/* Estado atual */}
      <div className="flex items-center gap-3 flex-wrap p-3 border border-tint rounded-lg bg-slate-50" data-testid="tg-status-card">
        <div className={`text-[11px] font-semibold px-2 py-1 rounded border ${status.cls}`}>
          Estado: {status.txt}
        </div>
        {tg.bot_username && (<div className="text-[11px] text-slate-600">Bot: <b>@{tg.bot_username}</b></div>)}
        {tg.chat_id_hint && (<div className="text-[11px] text-slate-600">Chat: <b>{tg.chat_id_hint}</b></div>)}
        <div className="ml-auto">
          <Btn variant="ghost" onClick={testarBot} disabled={testing || !tg.configured} testid="tg-test-btn">
            {testing ? "A enviar..." : "Enviar teste basico"}
          </Btn>
        </div>
      </div>

      {/* Editor bot_token / chat_id */}
      {canManage ? (
        <div className="border border-tint rounded-lg p-4" data-testid="tg-editor">
          <h4 className="text-navy font-semibold text-sm mb-1">Credenciais do bot</h4>
          <p className="text-xs text-muted2 mb-3">
            Gratis. Crie um bot em @BotFather, adicione-o ao chat/grupo e cole aqui o token + chat ID. As credenciais sao guardadas cifradas.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-navy mb-1">Bot token</label>
              <input type="password" data-testid="tg-canais-bot-token" value={tgForm.bot_token} autoComplete="new-password"
                onChange={(e) => setTgForm({ ...tgForm, bot_token: e.target.value })}
                placeholder={tg?.configured ? "(guardado - preencher para alterar)" : "123456:ABC-DEF..."}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-navy mb-1">Chat ID {tg?.chat_id_hint && <span className="text-muted2 font-normal">(atual {tg.chat_id_hint})</span>}</label>
              <input data-testid="tg-canais-chat-id" value={tgForm.chat_id}
                onChange={(e) => setTgForm({ ...tgForm, chat_id: e.target.value })}
                placeholder="-1001234567890 ou 123456789"
                className="w-full px-3 py-2 text-sm font-mono border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue/20 focus:outline-none" />
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm text-navy font-medium">
            <input type="checkbox" data-testid="tg-canais-enabled" checked={tgForm.enabled}
              onChange={(e) => setTgForm({ ...tgForm, enabled: e.target.checked })} className="w-4 h-4" />
            Enviar alertas por Telegram
          </label>
          <div className="flex gap-2 mt-3">
            <Btn variant="ghost" onClick={() => saveTelegram(true)} disabled={saving || !tgForm.bot_token || !tgForm.chat_id} testid="tg-canais-test">
              Testar
            </Btn>
            <Btn onClick={() => saveTelegram(false)} disabled={saving || !tgForm.bot_token || !tgForm.chat_id} testid="tg-canais-save">
              {saving ? "A guardar..." : "Guardar credenciais"}
            </Btn>
          </div>
        </div>
      ) : (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-800" data-testid="tg-editor-no-perm">
          Sem permissao para editar credenciais do bot. Contacte um super-admin.
        </div>
      )}

      {/* Fontes de alerta ativas */}
      <div className="border border-tint rounded-lg p-4" data-testid="tg-sources">
        <h4 className="text-navy font-semibold text-sm mb-1">Fontes de alerta ativas</h4>
        <p className="text-xs text-muted2 mb-3">
          Todas as fontes abaixo usam o mesmo bot. Quando o bot esta ativo, sao entregues automaticamente. Use "Testar" para simular uma mensagem etiquetada com a fonte.
        </p>
        <ul className="space-y-2">
          {TG_ALERT_SOURCES.map((s) => (
            <li key={s.key} className="flex items-start gap-3 border border-tint rounded-lg p-3" data-testid={`tg-source-${s.key}`}>
              <div className={`mt-1 h-2.5 w-2.5 rounded-full ${tg.configured && tg.enabled ? "bg-emerald-500" : "bg-slate-300"}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-navy">{s.label}</div>
                <div className="text-[11px] text-muted2">{s.desc}</div>
              </div>
              <Btn variant="ghost" onClick={() => testarFonte(s)} disabled={!tg.configured || !tg.enabled || testingSource === s.key} testid={`tg-source-test-${s.key}`}>
                {testingSource === s.key ? "..." : "Testar"}
              </Btn>
            </li>
          ))}
        </ul>
      </div>

      {/* Supervisor SLA */}
      <div className="border border-tint rounded-lg p-4" data-testid="tg-supervisor">
        <h4 className="text-navy font-semibold text-sm mb-1">Supervisor SLA (escalada por atraso)</h4>
        <p className="text-xs text-muted2 mb-3">
          Quando um pedido de handoff ultrapassa o SLA, o sistema envia alerta email + Telegram ao supervisor de 5 em 5 min.
          Se deixar o chat ID do supervisor em branco, usa o chat global acima.
        </p>
        <div className="grid md:grid-cols-2 gap-3">
          <Field
            label="Email do supervisor"
            value={sla.supervisor_email || ""}
            onChange={(v) => setSla({ ...sla, supervisor_email: v })}
            placeholder="supervisor@cogniseg.com"
            testid="tg-supervisor-email"
          />
          <Field
            label="Chat ID Telegram do supervisor (opcional)"
            value={sla.supervisor_telegram_chat_id || ""}
            onChange={(v) => setSla({ ...sla, supervisor_telegram_chat_id: v })}
            placeholder="ex: -1001234567890"
            testid="tg-supervisor-chat"
          />
        </div>
        <Btn onClick={saveSupervisor} disabled={savingSup} testid="tg-supervisor-save" className="mt-2">
          {savingSup ? "A guardar..." : "Guardar supervisor SLA"}
        </Btn>
      </div>

      <CallFollowupAlertCard />
      <DailyDigestCard />
      <TaskOverdueTodayAlertCard />
    </div>
  );
}

function TaskOverdueTodayAlertCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/team/task-overdue-today-alert/config"); setCfg(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/team/task-overdue-today-alert/config", {
        ativo: !!cfg.ativo,
        intervalo_minutos: Number(cfg.intervalo_minutos) || 30,
        horario_inicio: cfg.horario_inicio || "08:00",
        horario_fim: cfg.horario_fim || "20:00",
      });
      setCfg(data);
      toast.success("Preferencias do alerta guardadas.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/team/task-overdue-today-alert/run-now");
      if (data.alertados) toast.success(`${data.alertados} tarefa(s) com hora passada identificada(s). Alerta Telegram enviado.`);
      else toast.message("Sem tarefas de hoje com hora passada neste momento.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  if (!cfg) return null;
  return (
    <div className="border border-tint rounded-lg p-4" data-testid="tg-task-overdue-today">
      <h4 className="text-navy font-semibold text-sm mb-1">Tarefas de hoje com hora passada</h4>
      <p className="text-xs text-muted2 mb-3">
        Quando uma tarefa com prazo para hoje passa a hora prevista sem ter sido iniciada (estado "por fazer"),
        o Hub envia 1 alerta Telegram consolidado ao canal central (o mesmo usado pelos outros alertas).
      </p>
      <label className="flex items-center gap-2 text-sm text-navy font-medium mb-3">
        <input type="checkbox" checked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })}
          data-testid="tha-ativo" className="w-4 h-4" />
        Alerta ativo
      </label>
      <div className="grid md:grid-cols-3 gap-3">
        <Selecter label="Frequencia de verificacao" value={String(cfg.intervalo_minutos)}
          onChange={(v) => setCfg({ ...cfg, intervalo_minutos: Number(v) })}
          options={["15", "30", "60", "120"]} testid="tha-intervalo" />
        <Field label="Horario inicio (nao perturbar antes)" type="time" value={cfg.horario_inicio}
          onChange={(v) => setCfg({ ...cfg, horario_inicio: v })} testid="tha-horario-inicio" />
        <Field label="Horario fim (nao perturbar depois)" type="time" value={cfg.horario_fim}
          onChange={(v) => setCfg({ ...cfg, horario_fim: v })} testid="tha-horario-fim" />
      </div>
      <div className="flex gap-2 mt-3">
        <Btn variant="ghost" onClick={runNow} disabled={running} testid="tha-run-now">
          {running ? "A verificar..." : "Verificar agora"}
        </Btn>
        <Btn onClick={save} disabled={saving} testid="tha-save">
          {saving ? "A guardar..." : "Guardar"}
        </Btn>
      </div>
    </div>
  );
}

function DailyDigestCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/crm/call-followup-digest/config"); setCfg(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/crm/call-followup-digest/config", { ativo: !!cfg.ativo });
      setCfg(data);
      toast.success("Preferencia de resumo diario guardada.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/crm/call-followup-digest/run-now");
      if (data.enviado === false) {
        toast.message(data.motivo === "sem_tarefas" ? "Sem tarefas de recontacto para incluir no resumo." : "Resumo nao enviado.");
      } else {
        toast.success(`Resumo enviado - ${data.novas ?? 0} nova(s), ${data.pendentes ?? 0} pendente(s), ${data.atrasadas ?? 0} atrasada(s).`);
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  if (!cfg) return null;
  return (
    <div className="border border-tint rounded-lg p-4" data-testid="tg-daily-digest">
      <h4 className="text-navy font-semibold text-sm mb-1">Resumo diario de tarefas de recontacto</h4>
      <p className="text-xs text-muted2 mb-3">
        Envia por Telegram e email (super-admins) um resumo das tarefas de recontacto novas (ultimas 24h) e pendentes,
        2x por dia (09h00 e 15h00, hora de Lisboa).
      </p>
      <label className="flex items-center gap-2 text-sm text-navy font-medium mb-1">
        <input type="checkbox" checked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })}
          data-testid="dd-ativo" className="w-4 h-4" />
        Resumo diario ativo
      </label>
      <p className="text-[11px] text-muted2 mb-3" data-testid="dd-ultimo-envio">
        Ultimo envio: {cfg.ultimo_envio ? new Date(cfg.ultimo_envio).toLocaleString("pt-PT") : "ainda nao enviado"}
        {cfg.ultimo_envio && (
          <span className="ml-1">
            ({cfg.ultimo_novas ?? 0} nova(s), {cfg.ultimo_pendentes ?? 0} pendente(s), {cfg.ultimo_atrasadas ?? 0} atrasada(s))
          </span>
        )}
      </p>
      <div className="flex gap-2">
        <Btn variant="ghost" onClick={runNow} disabled={running} testid="dd-run-now">
          {running ? "A enviar..." : "Enviar resumo agora"}
        </Btn>
        <Btn onClick={save} disabled={saving} testid="dd-save">
          {saving ? "A guardar..." : "Guardar"}
        </Btn>
      </div>
    </div>
  );
}

function CallFollowupAlertCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/crm/call-followup-alert/config"); setCfg(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/crm/call-followup-alert/config", {
        ativo: !!cfg.ativo,
        horas_espera: Number(cfg.horas_espera) || 24,
        intervalo_minutos: Number(cfg.intervalo_minutos) || 30,
        horario_inicio: cfg.horario_inicio || "08:00",
        horario_fim: cfg.horario_fim || "20:00",
        criar_tarefa: !!cfg.criar_tarefa,
      });
      setCfg(data);
      toast.success("Preferencias de alerta guardadas.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/crm/call-followup-alert/run-now");
      if (data.alertados) {
        toast.success(`${data.alertados} chamada(s) sem recontacto identificada(s)${data.tarefas_criadas ? ` - ${data.tarefas_criadas} tarefa(s) criada(s)` : ""}. Alerta Telegram enviado.`);
      } else {
        toast.message("Sem chamadas sem recontacto neste momento.");
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  if (!cfg) return null;
  return (
    <div className="border border-tint rounded-lg p-4" data-testid="tg-call-followup">
      <h4 className="text-navy font-semibold text-sm mb-1">Chamadas sem recontacto</h4>
      <p className="text-xs text-muted2 mb-3">
        Quando uma chamada fica marcada como "Nao atendeu" e ninguem a recontacta dentro do prazo definido,
        o Hub envia 1 alerta Telegram e cria automaticamente uma tarefa de recontacto para o autor da chamada.
      </p>
      <label className="flex items-center gap-2 text-sm text-navy font-medium mb-3">
        <input type="checkbox" checked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })}
          data-testid="cfa-ativo" className="w-4 h-4" />
        Alerta ativo
      </label>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Alertar apos X horas sem recontacto" type="number" value={cfg.horas_espera}
          onChange={(v) => setCfg({ ...cfg, horas_espera: v })} testid="cfa-horas-espera" />
        <Selecter label="Frequencia de verificacao" value={String(cfg.intervalo_minutos)}
          onChange={(v) => setCfg({ ...cfg, intervalo_minutos: Number(v) })}
          options={["15", "30", "60", "120"]} testid="cfa-intervalo" />
        <Field label="Horario inicio (nao perturbar antes)" type="time" value={cfg.horario_inicio}
          onChange={(v) => setCfg({ ...cfg, horario_inicio: v })} testid="cfa-horario-inicio" />
        <Field label="Horario fim (nao perturbar depois)" type="time" value={cfg.horario_fim}
          onChange={(v) => setCfg({ ...cfg, horario_fim: v })} testid="cfa-horario-fim" />
      </div>
      <label className="flex items-center gap-2 mt-3 text-sm text-navy font-medium">
        <input type="checkbox" checked={!!cfg.criar_tarefa} onChange={(e) => setCfg({ ...cfg, criar_tarefa: e.target.checked })}
          data-testid="cfa-criar-tarefa" className="w-4 h-4" />
        Criar tarefa automatica de recontacto (CRM &gt; Tarefas)
      </label>
      <div className="flex gap-2 mt-3">
        <Btn variant="ghost" onClick={runNow} disabled={running} testid="cfa-run-now">
          {running ? "A verificar..." : "Verificar agora"}
        </Btn>
        <Btn onClick={save} disabled={saving} testid="cfa-save">
          {saving ? "A guardar..." : "Guardar"}
        </Btn>
      </div>
    </div>
  );
}

function BulkSyncBtn() {
  const [state, setState] = useState(null);
  const [force, setForce] = useState(false);
  const [resetIds, setResetIds] = useState(false);

  const run = async () => {
    setState("running");
    try {
      const { data } = await api.post("/channels/letscall/sync-all-contacts", { force: force || resetIds, reset_ids: resetIds });
      setState(data);
      if (data.ok) {
        const parts = [];
        if (data.newly_synced) parts.push(`${data.newly_synced} criados`);
        if (data.updated) parts.push(`${data.updated} atualizados`);
        if (data.already_synced) parts.push(`${data.already_synced} saltados`);
        const msg = parts.length ? parts.join(", ") + "." : "Sincronizacao concluida.";
        toast.success(msg);
        if (resetIds) setResetIds(false);
      }
    } catch (e) {
      setState(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao sincronizar contactos.");
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Btn variant="ghost" onClick={run} disabled={state === "running"} testid="lc-bulk-sync">
          {state === "running" ? "A sincronizar..." : "Sincronizar contactos CRM"}
        </Btn>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={force} onChange={(e) => { setForce(e.target.checked); if (!e.target.checked) setResetIds(false); }} data-testid="lc-bulk-sync-force" />
          Re-sincronizar todos
        </label>
        {force && (
          <label className="flex items-center gap-1.5 text-xs text-amber-700 cursor-pointer" title="Limpa os IDs guardados e cria de raiz no LetsCall (util se a chave API ou workspace foram alterados)">
            <input type="checkbox" checked={resetIds} onChange={(e) => setResetIds(e.target.checked)} data-testid="lc-bulk-sync-reset" />
            Sync limpo (resetar IDs)
          </label>
        )}
      </div>
      {state && state !== "running" && (
        <div className="text-xs text-slate-600 bg-tint rounded px-2 py-1.5 inline-flex gap-3 flex-wrap" data-testid="lc-bulk-sync-result">
          <span>Total: <strong>{state.total}</strong></span>
          {state.newly_synced > 0 && <span className="text-emerald-700">Criados: <strong>{state.newly_synced}</strong></span>}
          {state.updated > 0 && <span className="text-blue-700">Atualizados: <strong>{state.updated}</strong></span>}
          {state.already_synced > 0 && <span className="text-slate-500">Saltados: <strong>{state.already_synced}</strong></span>}
          {state.errors > 0 && <span className="text-red-600">Erros: <strong>{state.errors}</strong></span>}
        </div>
      )}
    </div>
  );
}

function LetsCallTab() {
  const [cfg, setCfg] = useState({ ativo: true, api_configured: false, webhook_secret_set: false, agent_extension: "", sender_id: "", api_key_masked: "" });
  const [apiKey, setApiKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [testing, setTesting] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const webhookUrl = `${backend}/api/webhooks/letscall`;

  const load = async () => {
    try { const { data } = await api.get("/channels/letscall/config"); setCfg((p) => ({ ...p, ...data })); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try {
      await api.put("/channels/letscall/config", {
        ativo: cfg.ativo,
        api_key: apiKey || undefined,
        webhook_secret: webhookSecret || undefined,
        agent_extension: cfg.agent_extension,
        sender_id: cfg.sender_id,
      });
      toast.success("LetsCall One configurado.");
      setApiKey(""); setWebhookSecret(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const testar = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/channels/letscall/test");
      toast[data.ok ? "success" : "warning"](data.ok ? "Ligacao OK - API Key valida." : `Falhou: ${data.erro || "verifique a API Key"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const copyWebhook = () => { navigator.clipboard.writeText(webhookUrl); toast.success("URL copiado."); };

  return (
    <div className="max-w-3xl space-y-6" data-testid="letscall-config">
      {/* Estado */}
      <div className="flex items-center gap-3">
        <Badge value={cfg.api_configured ? "configurado" : "nao_configurado"} />
        {cfg.api_configured && <Badge value={cfg.webhook_secret_set ? "webhook_seguro" : "webhook_sem_secret"} />}
      </div>

      {/* Instrucoes */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900 space-y-2">
        <p className="font-semibold">Como configurar no portal LetsCall One:</p>
        <ol className="list-decimal list-inside space-y-1 text-xs">
          <li>Aceda a <a href="https://one.letscall.io" target="_blank" rel="noreferrer" className="underline font-medium">one.letscall.io</a> com conta Administrador.</li>
          <li>Va a Configuracoes - API Keys - Nova Chave. Ative: <code>calls.originate</code>, <code>transcriptions.read</code>, <code>contacts.read</code>, <code>contacts.write</code>, <code>sms.send</code>.</li>
          <li>Copie a chave <code>lco_xxx</code> (mostra apenas uma vez) e cole abaixo.</li>
          <li>Va a Configuracoes - Integracoes - Webhooks - Novo Webhook. URL: o campo abaixo. Ative <code>call.ended</code>, <code>call.dispositioned</code>. Crie um Secret e cole abaixo.</li>
        </ol>
      </div>

      {/* URL do Webhook */}
      <div>
        <label className="block text-xs font-semibold text-navy mb-1">URL do Webhook (copie para o LetsCall One)</label>
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-tint rounded-lg p-2 text-[11px] break-all text-navy font-mono" data-testid="letscall-webhook-url">{webhookUrl}</div>
          <Btn variant="ghost" onClick={copyWebhook} testid="lc-copy-webhook">Copiar</Btn>
        </div>
      </div>

      {/* Canal ativo */}
      <label className="flex items-center gap-2 text-sm text-navy">
        <input type="checkbox" checked={!!cfg.ativo} onChange={(e) => setCfg({ ...cfg, ativo: e.target.checked })} data-testid="lc-ativo" />
        Canal ativo
      </label>

      {/* Credenciais */}
      <div className="border-t border-tint pt-5 space-y-4">
        <h4 className="text-navy font-semibold text-sm">Credenciais (guardadas cifradas)</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-navy mb-1">API Key (lco_...)</label>
            <input
              type="password"
              className="w-full border border-tint rounded-lg px-3 py-2 text-sm"
              placeholder={cfg.api_key_masked || cfg.api_configured ? "****  (deixe vazio para manter)" : "lco_xxxxxxxxxxxxxxxx"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="lc-apikey"
            />
            <p className="text-[10px] text-muted2 mt-1">Permissoes necessarias: calls.originate, transcriptions.read, contacts.read/write, sms.send</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy mb-1">Webhook Secret (HMAC)</label>
            <input
              type="password"
              className="w-full border border-tint rounded-lg px-3 py-2 text-sm"
              placeholder={cfg.webhook_secret_set ? "****  (deixe vazio para manter)" : "secret gerado no LetsCall One"}
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              data-testid="lc-webhook-secret"
            />
            <p className="text-[10px] text-muted2 mt-1">Secret definido no webhook do portal LetsCall One para validacao HMAC-SHA256.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy mb-1">Extensao do agente (click-to-call)</label>
            <input
              className="w-full border border-tint rounded-lg px-3 py-2 text-sm"
              placeholder="ex: 201"
              value={cfg.agent_extension}
              onChange={(e) => setCfg({ ...cfg, agent_extension: e.target.value })}
              data-testid="lc-extension"
            />
            <p className="text-[10px] text-muted2 mt-1">Extensao VoIP do agente por defeito para click-to-call.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-navy mb-1">Sender ID (SMS, max 11 chars)</label>
            <input
              className="w-full border border-tint rounded-lg px-3 py-2 text-sm"
              placeholder="ex: Cogniseg"
              maxLength={11}
              value={cfg.sender_id}
              onChange={(e) => setCfg({ ...cfg, sender_id: e.target.value })}
              data-testid="lc-sender-id"
            />
            <p className="text-[10px] text-muted2 mt-1">Remetente SMS alfanumerico (ou numero de telefone).</p>
          </div>
        </div>
      </div>

      {/* Acoes */}
      <div className="flex gap-2 flex-wrap">
        <Btn onClick={save} testid="lc-save">Guardar LetsCall One</Btn>
        <Btn variant="ghost" onClick={testar} disabled={testing || !cfg.api_configured} testid="lc-test">
          {testing ? "A testar..." : "Testar ligacao"}
        </Btn>
        {cfg.api_configured && (
          <BulkSyncBtn />
        )}
      </div>

      {/* Info sobre chamadas no CRM */}
      {cfg.api_configured && (
        <div className="bg-tint rounded-lg p-4 text-xs text-navy space-y-1">
          <p className="font-semibold">Funcionalidades ativas:</p>
          <ul className="list-disc list-inside space-y-0.5 text-muted2">
            <li>Click-to-call via botao "Ligar via LetsCall" nas fichas CRM.</li>
            <li>Cada chamada concluida cria automaticamente uma atividade CRM (tipo Chamada).</li>
            <li>Tipificacoes do agente (call.dispositioned) actualizam a atividade com notas.</li>
            <li>Sincronizacao de contactos CRM para LetsCall One (botao no drawer do contacto).</li>
          </ul>
        </div>
      )}
    </div>
  );
}

function ChatWidgetTab() {
  const [chat, setChat] = useState({ titulo: "Fale connosco", saudacao: "", cor: "#0B2545", auto_reply: "" });
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const snippet = `<script src="${backend}/api/public/chat-widget.js" async></script>`;
  const load = async () => { try { const { data } = await api.get("/channels/chat/config"); setChat((p) => ({ ...p, ...data })); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async () => { try { await api.put("/channels/chat/config", { titulo: chat.titulo, saudacao: chat.saudacao, cor: chat.cor, auto_reply: chat.auto_reply }); toast.success("Widget de chat guardado."); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const copy = () => { navigator.clipboard.writeText(snippet); toast.success("Snippet copiado."); };
  return (
    <div className="max-w-3xl space-y-4" data-testid="chat-config">
      <p className="text-xs text-muted2">Personalize e cole este snippet no seu site. As mensagens geram leads automaticamente no CRM (origem chat), enviam uma resposta automatica ao contacto e notificam a equipa comercial por email.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Titulo" value={chat.titulo} onChange={(v) => setChat({ ...chat, titulo: v })} testid="chat-titulo" />
        <Field label="Cor (hex)" value={chat.cor} onChange={(v) => setChat({ ...chat, cor: v })} testid="chat-cor" />
      </div>
      <Field label="Saudacao (mostrada no widget)" value={chat.saudacao} onChange={(v) => setChat({ ...chat, saudacao: v })} testid="chat-saudacao" />
      <Field label="Resposta automatica (email ao contacto)" value={chat.auto_reply} onChange={(v) => setChat({ ...chat, auto_reply: v })} testid="chat-autoreply" />
      <Btn onClick={save} testid="chat-save">Guardar widget</Btn>
      <div className="border-t border-tint pt-3">
        <label className="block text-sm font-semibold text-navy mb-1">Snippet</label>
        <div className="flex items-start gap-2">
          <code className="flex-1 bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] break-all" data-testid="chat-snippet">{snippet}</code>
          <Btn variant="ghost" onClick={copy} testid="chat-copy">Copiar</Btn>
        </div>
      </div>
    </div>
  );
}

function SmsTab() {
  const [s, setS] = useState({ configured: false, sender: "", base_url: "https://api.smsbuzz.net", send_path: "/sms/send", username_masked: "" });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [testTo, setTestTo] = useState("");
  const [wh, setWh] = useState({ segredo: "", ultimas_recebidas: [] });
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const webhookUrl = wh.segredo
    ? `${backendUrl}/api/webhooks/smsbuzz?token=${wh.segredo}`
    : "";

  const load = async () => {
    try {
      const { data } = await api.get("/sms/settings"); setS(data);
      const { data: w } = await api.get("/sms/webhook/info"); setWh(w);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    try { await api.put("/sms/settings", { username: username || undefined, password: password || undefined, sender: s.sender, base_url: s.base_url, send_path: s.send_path }); toast.success("Definicoes guardadas."); setUsername(""); setPassword(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const test = async () => {
    if (!testTo) return toast.error("Indique um numero (ex.: 9XXXXXXXX ou +351...).");
    try { const { data } = await api.post("/sms/test", { para: testTo }); toast[data.estado === "enviado" ? "success" : "message"](`Estado: ${data.estado}${data.erro ? " - " + data.erro : ""}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const copyWebhook = async () => {
    if (!webhookUrl) return;
    try { await navigator.clipboard.writeText(webhookUrl); toast.success("URL copiado."); }
    catch { toast.message("Nao foi possivel copiar. Selecione e copie manualmente."); }
  };
  const regen = async () => {
    if (!window.confirm("Regenerar o segredo do webhook? O URL antigo deixa de ser aceite.")) return;
    try { const { data } = await api.post("/sms/webhook/regenerate"); setWh(data); toast.success("Novo segredo gerado."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const clearInbox = async () => {
    if (!window.confirm("Limpar o historico do webhook (mensagens recebidas e reports de entrega)? A timeline do CRM nao e afectada.")) return;
    try { await api.delete("/sms/webhook/inbox"); toast.success("Historico limpo."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="sms-settings">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-navy">Estado:</span>
        <Badge value={s.configured ? "ativo" : "nao_configurado"} />
        {s.username_masked && <span className="text-xs text-muted2">Utilizador: {s.username_masked}</span>}
      </div>
      <p className="text-xs text-muted2">Envio via SMS Buzz (Let's Call). Doc oficial: <a href="https://developers.smsbuzz.pt/" target="_blank" rel="noreferrer" className="text-blue underline">developers.smsbuzz.pt</a>. As credenciais sao guardadas cifradas.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Utilizador (deixe vazio para manter)" type="password" value={username} onChange={setUsername} placeholder="utilizador SMS Buzz" testid="sb-user" />
        <Field label="Password (deixe vazio para manter)" type="password" value={password} onChange={setPassword} placeholder="****" testid="sb-pass" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Remetente (SenderName)" value={s.sender} onChange={(v) => setS({ ...s, sender: v })} placeholder="Cogniseg" testid="sb-sender" />
        <Field label="Base URL da API" value={s.base_url} onChange={(v) => setS({ ...s, base_url: v })} placeholder="https://api.smsbuzz.net" testid="sb-base" />
        <Field label="Caminho de envio" value={s.send_path} onChange={(v) => setS({ ...s, send_path: v })} placeholder="/sms/send" testid="sb-path" />
      </div>
      <Btn onClick={save} testid="sb-save">Guardar definicoes</Btn>

      <div className="flex items-end gap-2 border-t border-tint pt-4">
        <div className="flex-1"><Field label="Enviar SMS de teste para" value={testTo} onChange={setTestTo} placeholder="9XXXXXXXX ou +3519..." testid="sb-test-to" /></div>
        <Btn variant="ghost" onClick={test} testid="sb-test-btn">Enviar teste</Btn>
      </div>

      {/* Webhook (MO + DLR) */}
      <div className="border-t border-tint pt-4 space-y-2" data-testid="sms-webhook">
        <div className="flex items-center justify-between">
          <h4 className="text-navy font-semibold text-sm">Webhook de respostas (MO + DLR)</h4>
          <Btn variant="ghost" onClick={regen} testid="sb-webhook-regen" className="text-xs">Regenerar segredo</Btn>
        </div>
        <p className="text-[11px] text-muted2">
          Cole este URL no painel do SMS Buzz para receber respostas dos destinatarios (Mobile Originated) e reports de entrega (DLR).
          O parametro <code className="font-mono bg-slate-100 px-1 rounded">?token=</code> garante que so pedidos legitimos sao aceites.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 min-w-0 text-[11px] font-mono bg-slate-100 border border-tint rounded px-2 py-1.5 truncate" data-testid="sb-webhook-url">{webhookUrl || "-"}</code>
          <Btn variant="ghost" onClick={copyWebhook} testid="sb-webhook-copy" disabled={!webhookUrl}>Copiar</Btn>
        </div>
        <div className="mt-2">
          <div className="text-[11px] font-semibold text-navy mb-1 flex items-center justify-between">
            <span>Ultimas mensagens ({wh.ultimas_recebidas?.length || 0})</span>
            {(wh.ultimas_recebidas || []).length > 0 && (
              <Btn variant="ghost" onClick={clearInbox} testid="sb-inbox-clear" className="text-[10px] px-2 py-0.5">Limpar historico</Btn>
            )}
          </div>
          <div className="text-[10px] text-muted2 mb-1.5">
            <span className="font-semibold text-emerald-700">MO</span> = resposta recebida do destinatario. <span className="font-semibold text-sky-700">DLR</span> = confirmacao de entrega da operadora (envios feitos por si).
          </div>
          {(wh.ultimas_recebidas || []).length === 0 ? (
            <div className="text-[11px] text-muted2 py-2">Sem mensagens ainda. Envie uma SMS de teste e responda para validar o webhook.</div>
          ) : (
            <div className="border border-tint rounded max-h-72 overflow-y-auto divide-y divide-tint" data-testid="sb-inbox-list">
              {wh.ultimas_recebidas.map((m) => {
                const isDlr = m.tipo === "dlr";
                const numero = isDlr ? m.to : m.from;
                const statusColor = (m.status || "").toLowerCase() === "delivered" ? "text-emerald-700"
                  : (m.status || "").toLowerCase().includes("fail") ? "text-red-600"
                  : "text-slate-600";
                return (
                  <div key={m.id} className="px-2.5 py-2 text-[11.5px] hover:bg-slate-50/60">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${isDlr ? "bg-sky-50 text-sky-700 border border-sky-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>{m.tipo}</span>
                      {numero ? (
                        <span className="font-mono text-slate-700 font-semibold">{isDlr ? "-> " : "<- "}{numero}</span>
                      ) : (
                        <span className="text-muted2 italic">sem numero</span>
                      )}
                      {m._conta_nome && (
                        <a href={`/app/crm/contas/${m.account_id}`} className="text-blue text-[10.5px] hover:underline">({m._conta_nome})</a>
                      )}
                      {isDlr && m.status && (
                        <span className={`text-[10.5px] font-semibold ${statusColor}`}>- {m.status}</span>
                      )}
                      {m.ref_id && (
                        <span className="text-[10px] text-muted2 font-mono">ref {m.ref_id}</span>
                      )}
                      <span className="text-muted2 ml-auto whitespace-nowrap">{m.em ? new Date(m.em).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "-"}</span>
                    </div>
                    {m.text && !isDlr && (
                      <div className="text-slate-700 mt-1 whitespace-pre-wrap break-words">"{m.text}"</div>
                    )}
                    {isDlr && m._original_texto && (
                      <div className="text-slate-500 mt-1 italic truncate">envio original: "{m._original_texto}"</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <SmsTemplatesSection />
    </div>
  );
}

function SmsTemplatesSection() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => { try { const { data } = await api.get("/sms/templates"); setRows(data || []); } catch { /* noop */ } };
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!editing?.nome?.trim() || !editing?.corpo?.trim()) return toast.error("Nome e corpo obrigatorios.");
    const vars = Array.from(new Set([...(editing.corpo.matchAll(/\{\{\s*([\w_-]+)\s*\}\}/g))].map(m => m[1])));
    const payload = { nome: editing.nome.trim(), corpo: editing.corpo, variaveis: vars };
    try {
      if (editing.id) await api.put(`/sms/templates/${editing.id}`, payload);
      else await api.post("/sms/templates", payload);
      toast.success("Guardado."); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Eliminar template?")) return;
    try { await api.delete(`/sms/templates/${id}`); load(); toast.success("Eliminado."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="border-t border-tint pt-4 space-y-3" data-testid="sms-templates">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-navy font-semibold text-sm">Templates de SMS</h4>
          <p className="text-[11px] text-muted2">Reutilizaveis com variaveis {"{{"}{"nome"}{"}}"}, {"{{"}{"primeiro_nome"}{"}}"}, {"{{"}{"empresa"}{"}}"}, {"{{"}{"email"}{"}}"}, {"{{"}{"nif"}{"}}"}, {"{{"}{"cidade"}{"}}"}.</p>
        </div>
        <Btn variant="ghost" onClick={() => setEditing({ nome: "", corpo: "" })} testid="sms-tpl-new">Novo template</Btn>
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-muted2 py-2">Sem templates. Crie um para uso rapido no composer.</div>
      ) : (
        <div className="border border-tint rounded overflow-hidden">
          {rows.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-3 py-2 border-b border-tint last:border-b-0" data-testid={`sms-tpl-${t.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-navy">{t.nome}</div>
                <div className="text-[11px] text-slate-500 truncate">{t.corpo}</div>
                {(t.variaveis || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {t.variaveis.map((v) => <span key={v} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-blue/10 text-blue">{v}</span>)}
                  </div>
                )}
              </div>
              <Btn variant="ghost" onClick={() => setEditing(t)} testid={`sms-tpl-edit-${t.id}`} className="text-xs">Editar</Btn>
              <button onClick={() => remove(t.id)} data-testid={`sms-tpl-del-${t.id}`} className="text-[11px] text-red-600 hover:underline">Eliminar</button>
            </div>
          ))}
        </div>
      )}
      {editing && (
        <div className="border border-blue/40 bg-blue/5 rounded-lg p-3 space-y-2" data-testid="sms-tpl-editor">
          <input value={editing.nome} onChange={(e) => setEditing({ ...editing, nome: e.target.value })}
            placeholder="Nome do template" data-testid="sms-tpl-nome"
            className="w-full px-3 py-1.5 text-sm border border-tint rounded" />
          <textarea value={editing.corpo} onChange={(e) => setEditing({ ...editing, corpo: e.target.value })}
            rows={3} placeholder="Ola {{primeiro_nome}}, ..."
            data-testid="sms-tpl-corpo"
            className="w-full px-3 py-1.5 text-sm border border-tint rounded font-mono" />
          <div className="flex gap-2">
            <Btn onClick={save} testid="sms-tpl-save">Guardar</Btn>
            <Btn variant="ghost" onClick={() => setEditing(null)} testid="sms-tpl-cancel">Cancelar</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function SLATab() {
  const [s, setS] = useState({ alta: 4, media: 24, baixa: 72 });
  const load = async () => {
    try { const { data } = await api.get("/tickets/sla-config"); setS({ alta: data.alta || 4, media: data.media || 24, baixa: data.baixa || 72 }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async () => {
    try {
      await api.put("/tickets/sla-config", { alta: parseFloat(s.alta) || 4, media: parseFloat(s.media) || 24, baixa: parseFloat(s.baixa) || 72 });
      toast.success("SLA guardado."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-3xl space-y-5" data-testid="sla-settings">
      <p className="text-sm text-muted2">Prazo maximo (em horas) para a primeira resposta a um ticket, por prioridade. Ao ultrapassar, o ticket e marcado como "SLA excedido" e o responsavel recebe alerta no sino. Avisos surgem tambem quando faltam 2 horas ou menos.</p>
      <div className="grid md:grid-cols-3 gap-4">
        <Field label="Prioridade alta (horas)" type="number" value={s.alta} onChange={(v) => setS({ ...s, alta: v })} testid="sla-alta" />
        <Field label="Prioridade media (horas)" type="number" value={s.media} onChange={(v) => setS({ ...s, media: v })} testid="sla-media" />
        <Field label="Prioridade baixa (horas)" type="number" value={s.baixa} onChange={(v) => setS({ ...s, baixa: v })} testid="sla-baixa" />
      </div>
      <Btn onClick={save} testid="sla-save">Guardar SLA</Btn>
    </div>
  );
}

function PlataformasTab() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);      // objeto de plataforma em edicao
  const [creds, setCreds] = useState(null);    // plataforma em edicao de credenciais
  const load = () => api.get("/platforms").then(({ data }) => setRows(data || [])).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  const empty = { nome: "", slug: "", descricao: "", ativo: true, tipo: "interno", api_url: "", webhook_url: "", contacto_tecnico: "", logo_url: "", observacoes: "", is_api_connector: false, ui_path: "" };
  const openNew = () => setEdit({ ...empty });
  const remove = async (p) => {
    if (!window.confirm(`Eliminar plataforma "${p.nome}"?`)) return;
    try { await api.delete(`/platforms/${p.id}`); toast.success("Plataforma eliminada."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // Plataformas com pagina dedicada: usa `ui_path` do backend (auto-preenchido para
  // conectores API-based) ou fallback historico para cognietica/privaxia.
  const LEGACY_DEDICATED = { cognietica: "/app/cognietica", privaxia: "/app/privaxia" };
  const dedicatedFor = (p) => (p?.ui_path || "").trim() || LEGACY_DEDICATED[(p?.slug || "").toLowerCase()] || "";
  return (
    <div className="space-y-4" data-testid="platforms-settings">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted2 max-w-2xl">Plataformas do portfolio SaaS. Estes dados alimentam os dropdowns de "Plataforma" em todo o sistema. Os conectores dedicados (CogniEtica, Privaxia) configuram-se nas suas paginas proprias (link "Configurar" no card).</p>
        <Btn onClick={openNew} testid="plat-new"><Plus size={15} /> Nova plataforma</Btn>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.length === 0 && <p className="text-sm text-muted2">Sem plataformas registadas.</p>}
        {rows.map((p) => {
          const dedicated = dedicatedFor(p);
          return (
            <div key={p.id} className="border border-tint rounded-xl bg-white p-5 space-y-3" data-testid={`plat-card-${p.slug}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-navy font-bold tracking-tight">{p.nome}</h3>
                    <Badge value={p.ativo ? "ativa" : "inativa"} />
                    {dedicated && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue/10 text-blue font-semibold">conector dedicado</span>}
                  </div>
                  <div className="text-[11px] text-muted2 mt-0.5">slug: <code className="bg-tint px-1 rounded">{p.slug}</code> - {p.tipo || "interno"}</div>
                </div>
              </div>
              {p.descricao && <p className="text-xs text-muted2 leading-relaxed">{p.descricao}</p>}
              {!dedicated && (
                <div className="text-[11px] text-muted2 space-y-0.5">
                  {p.api_url && <div><b className="text-navy">API:</b> <span className="break-all">{p.api_url}</span></div>}
                  {p.contacto_tecnico && <div><b className="text-navy">Contacto tecnico:</b> {p.contacto_tecnico}</div>}
                  <div className="flex gap-2 pt-1">
                    <span className={p.api_key_configurado ? "text-emerald-600" : "text-muted2"}>{p.api_key_configurado ? "API key configurada" : "sem API key"}</span>
                    {p.webhook_secret_configurado && <span className="text-emerald-600">- webhook OK</span>}
                  </div>
                </div>
              )}
              {dedicated && (
                <p className="text-[11px] text-muted2 italic">Este conector tem uma pagina dedicada onde ficam a URL da API, a X-API-Key e o webhook signing secret.</p>
              )}
              <div className="flex flex-wrap gap-2 pt-2">
                {dedicated ? (
                  <a href={dedicated} data-testid={`plat-configure-${p.slug}`} className="text-xs px-2.5 py-1 rounded bg-blue text-white hover:bg-blue/90 transition-colors">
                    Configurar em {dedicated}
                  </a>
                ) : (
                  <>
                    <button onClick={() => setEdit({ ...p })} data-testid={`plat-edit-${p.slug}`} className="text-xs px-2.5 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white transition-colors">Editar</button>
                    <button onClick={() => setCreds(p)} data-testid={`plat-creds-${p.slug}`} className="text-xs px-2.5 py-1 rounded bg-blue/10 text-blue hover:bg-blue hover:text-white transition-colors">Credenciais</button>
                    <button onClick={() => remove(p)} data-testid={`plat-delete-${p.slug}`} className="text-xs px-2.5 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">Eliminar</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {edit && <PlataformaModal value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
      {creds && <PlataformaCredsModal plat={creds} onClose={() => setCreds(null)} onSaved={() => { setCreds(null); load(); }} />}
    </div>
  );
}

function PlataformaModal({ value, onClose, onSaved }) {
  const [p, setP] = useState(value);
  const isEdit = !!value.id;
  const save = async () => {
    if (!p.nome || !p.slug) return toast.error("Nome e slug sao obrigatorios.");
    try {
      const body = {
        nome: p.nome, slug: p.slug, descricao: p.descricao || "", ativo: !!p.ativo,
        tipo: p.tipo || "interno", api_url: p.api_url || "", webhook_url: p.webhook_url || "",
        contacto_tecnico: p.contacto_tecnico || "", logo_url: p.logo_url || "", observacoes: p.observacoes || "",
        is_api_connector: !!p.is_api_connector,
        ui_path: (p.ui_path || "").trim(),
      };
      if (isEdit) await api.put(`/platforms/${p.id}`, body); else await api.post("/platforms", body);
      toast.success(isEdit ? "Plataforma atualizada." : "Plataforma criada.");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title={isEdit ? `Editar ${value.nome}` : "Nova plataforma"} testid="plat-modal">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Nome" value={p.nome} onChange={(v) => setP({ ...p, nome: v })} testid="plat-nome" />
        <Field label="Slug" value={p.slug} onChange={(v) => setP({ ...p, slug: v })} testid="plat-slug" />
      </div>
      <TextArea label="Descricao" value={p.descricao} onChange={(v) => setP({ ...p, descricao: v })} testid="plat-desc" />
      <div className="grid md:grid-cols-2 gap-3">
        <Selecter label="Tipo" value={p.tipo || "interno"} onChange={(v) => setP({ ...p, tipo: v })} testid="plat-tipo" options={[{ value: "interno", label: "Interno" }, { value: "externo", label: "Externo (via API)" }]} />
        <Selecter label="Ativa" value={p.ativo ? "1" : "0"} onChange={(v) => setP({ ...p, ativo: v === "1" })} testid="plat-ativa" options={[{ value: "1", label: "Ativa" }, { value: "0", label: "Inativa" }]} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="URL da API (opcional)" value={p.api_url || ""} onChange={(v) => setP({ ...p, api_url: v })} testid="plat-api-url" />
        <Field label="URL de webhook (opcional)" value={p.webhook_url || ""} onChange={(v) => setP({ ...p, webhook_url: v })} testid="plat-webhook-url" />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Contacto tecnico" value={p.contacto_tecnico || ""} onChange={(v) => setP({ ...p, contacto_tecnico: v })} testid="plat-contacto" />
        <Field label="URL do logo (opcional)" value={p.logo_url || ""} onChange={(v) => setP({ ...p, logo_url: v })} testid="plat-logo" />
      </div>
      <TextArea label="Observacoes" value={p.observacoes || ""} onChange={(v) => setP({ ...p, observacoes: v })} testid="plat-obs" />
      <div className="border-t border-tint pt-3 mt-1">
        <div className="text-[11px] font-semibold text-slate-700 mb-2">Pagina dedicada (conector API-based)</div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700 mb-2">
          <input type="checkbox" checked={!!p.is_api_connector} onChange={(e) => setP({ ...p, is_api_connector: e.target.checked, ui_path: e.target.checked && !p.ui_path && p.slug ? (["cognietica","privaxia"].includes(p.slug.toLowerCase()) ? `/app/${p.slug.toLowerCase()}` : `/app/connectors/${p.slug.toLowerCase()}`) : (p.ui_path || "") })} data-testid="plat-is-api-connector" />
          Esta plataforma tem pagina dedicada de configuracao no Hub
        </label>
        <Field label="Rota da pagina (ex: /app/cognietica)" value={p.ui_path || ""} onChange={(v) => setP({ ...p, ui_path: v })} testid="plat-ui-path" />
        <p className="text-[10px] text-slate-500 mt-1">Se preenchido, o card em Definicoes mostra apenas o link "Configurar em ..." em vez dos campos inline (URL API, X-API-Key, webhook signing secret ficam nessa pagina).</p>
      </div>
      <div className="flex justify-end pt-3"><Btn onClick={save} testid="plat-save">Guardar</Btn></div>
    </Modal>
  );
}


function ManutencaoTab() {
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [purgePreview, setPurgePreview] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [pubUrl, setPubUrl] = useState("");
  const [pubUrlInferido, setPubUrlInferido] = useState("");
  const [pubUrlBusy, setPubUrlBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/maintenance/public-url");
        setPubUrl(data.public_url || "");
        setPubUrlInferido(data.inferido || "");
      } catch (_) { /* silent */ }
    })();
  }, []);

  const savePubUrl = async () => {
    setPubUrlBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/public-url", { public_url: pubUrl });
      setPubUrl(data.public_url || "");
      toast.success("URL publica actualizada.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao guardar.");
    } finally { setPubUrlBusy(false); }
  };

  const run = async (endpoint, label) => {
    setBusy(endpoint);
    setResult(null);
    try {
      const { data } = await api.post(endpoint);
      setResult({ endpoint, label, data });
      toast.success(`${label} concluida.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro na operacao.");
    } finally { setBusy(""); }
  };

  const loadPurgePreview = async () => {
    setPurgeBusy(true); setPurgeResult(null);
    try {
      const { data } = await api.get("/admin/maintenance/purge-test-data/preview");
      setPurgePreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao carregar pre-visualizacao.");
    } finally { setPurgeBusy(false); }
  };

  const confirmPurge = async () => {
    if (!purgePreview) return;
    const total = Object.values(purgePreview.counts || {}).reduce((a, b) => a + b, 0);
    if (total === 0) { toast("Nada a apagar."); return; }
    if (!window.confirm(`Vais eliminar definitivamente ${total} registos de teste (contas, contactos, deals, activities, handoffs, sessoes). Confirmas?`)) return;
    setPurgeBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/purge-test-data", { confirm: true });
      setPurgeResult(data);
      setPurgePreview(null);
      toast.success("Purga concluida.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro na purga.");
    } finally { setPurgeBusy(false); }
  };

  const tasks = [
    {
      key: "/admin/backfill-vies-address",
      title: "Corrigir moradas antigas",
      desc: "Re-parsa contas com morada concatenada (do tempo em que o VIES retornava tudo num campo unico) e preenche codigo_postal / localidade / cidade. Idempotente.",
      testid: "maint-backfill-vies",
    },
    {
      key: "/admin/backfill-upstream-plan-keys",
      title: "Alinhar chaves upstream dos planos",
      desc: "Sanitiza os SKU dos planos do catalogo para o formato ^[a-z0-9-]+$ exigido pelas plataformas (ex: 'P.CE-Business+' -> 'p-ce-business-plus').",
      testid: "maint-backfill-plans",
    },
    {
      key: "/admin/backfill-subscription-mrr",
      title: "Recalcular MRR das subscricoes",
      desc: "Preenche MRR + valor total das subscricoes activas com plano_id valido mas mrr=0.",
      testid: "maint-backfill-mrr",
    },
    {
      key: "/admin/seed-nis2ai-plans",
      title: "Criar planos default NIS2AI (CogNIS2 + COGNIACT + NIS2AI)",
      desc: "Cria 9 planos default (3 por produto: Starter, Pro, Enterprise/Premium) com precos indicativos (99/299/799 EUR para CogNIS2 e COGNIACT; 149/449/1199 EUR para NIS2AI Premium). Idempotente por SKU. Ajusta precos em /app/catalogo depois.",
      testid: "maint-seed-nis2ai-plans",
    },
  ];

  const COL_LABELS = {
    chat_handoffs: "Handoffs (chat/formulario)",
    chat_sessions: "Sessoes de chat",
    crm_accounts: "Contas CRM",
    crm_contacts: "Contactos CRM",
    crm_deals: "Deals",
    crm_activities: "Actividades CRM",
    email_log_fake: "Emails fake (DEV)",
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="maintenance-tab">
      <p className="text-sm text-muted2">Accoes administrativas para manutencao e correccao de dados historicos. Todas sao idempotentes - correr multiplas vezes e seguro.</p>

      {/* URL publica da aplicacao (para links em emails) */}
      <div className="border border-blue-200 bg-blue-50/50 rounded-xl p-5" data-testid="public-url-card">
        <h3 className="text-navy font-bold">URL publica da aplicacao</h3>
        <p className="text-xs text-muted2 mt-1 leading-relaxed">
          Endereco usado nos <b>emails de convite, reset de password</b> e outros links transacionais. Em producao aponta para o dominio real (ex.: <code>https://app.cogniseg.pt</code>). Se ficar em branco, o sistema usa o endereco do preview e os links poderao apontar para o ambiente errado.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <input type="url" value={pubUrl} onChange={(e) => setPubUrl(e.target.value)}
            placeholder="https://app.cogniseg.pt" data-testid="public-url-input"
            className="flex-1 px-3 py-2 border border-tint rounded-lg bg-white text-sm font-mono" />
          <Btn onClick={savePubUrl} disabled={pubUrlBusy} testid="public-url-save">
            {pubUrlBusy ? "A guardar..." : "Guardar"}
          </Btn>
        </div>
        {pubUrlInferido && !pubUrl && (
          <p className="text-[11px] text-amber-700 mt-2">
            Sem valor definido - actualmente os emails usam: <code>{pubUrlInferido}</code>. Preencha acima para forcar o dominio de producao.
          </p>
        )}
      </div>

      {/* Purga de dados de teste */}
      <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-5" data-testid="purge-test-data-card">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div className="flex-1">
            <h3 className="text-navy font-bold flex items-center gap-2">
              <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded uppercase">critico</span>
              Purgar dados de teste (handoffs + CRM cascata)
            </h3>
            <p className="text-xs text-muted2 mt-1 leading-relaxed">
              Elimina todos os <b>handoffs</b> (chat / formulario), <b>sessoes</b> associadas, e <b>contas / contactos / deals / actividades</b> cujos contactos tenham emails de teste (<code>@test.pt</code>, <code>@teste.pt</code>, <code>@x.pt</code>, <code>@y.pt</code>). Tambem apaga <code>email_log</code> com estado <code>enviado_fake</code>. Comeca sempre por Pre-visualizar.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <Btn variant="ghost" onClick={loadPurgePreview} disabled={purgeBusy} testid="purge-preview-btn">
              {purgeBusy && !purgePreview ? "A carregar..." : "Pre-visualizar"}
            </Btn>
            <Btn onClick={confirmPurge} disabled={!purgePreview || purgeBusy} testid="purge-confirm-btn"
                 className="bg-red-600 hover:bg-red-700">
              {purgeBusy && purgePreview ? "A apagar..." : "Eliminar (confirm)"}
            </Btn>
          </div>
        </div>

        {purgePreview && (
          <div className="mt-3 space-y-3" data-testid="purge-preview-panel">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {Object.entries(purgePreview.counts || {}).map(([k, v]) => (
                <div key={k} className={`rounded-lg border p-2 text-center ${v > 0 ? "bg-white border-red-200" : "bg-slate-50 border-tint opacity-60"}`}>
                  <div className={`text-lg font-bold ${v > 0 ? "text-red-600" : "text-muted2"}`}>{v}</div>
                  <div className="text-[10px] text-muted2 leading-tight">{COL_LABELS[k] || k}</div>
                </div>
              ))}
            </div>
            {(purgePreview.amostras?.crm_accounts?.length > 0 || purgePreview.amostras?.crm_contacts?.length > 0) && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted2 hover:text-navy">Amostras (primeiros 10 de cada)</summary>
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  {purgePreview.amostras?.crm_accounts?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase font-semibold text-muted2 mb-1">Contas</div>
                      <ul className="text-[11px] space-y-0.5">
                        {purgePreview.amostras.crm_accounts.map((a) => (
                          <li key={a.id} className="truncate">- <span className="font-semibold text-navy">{a.nome || "(sem nome)"}</span> <span className="text-muted2">({a.criado_em?.slice(0, 10)})</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {purgePreview.amostras?.crm_contacts?.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase font-semibold text-muted2 mb-1">Contactos</div>
                      <ul className="text-[11px] space-y-0.5">
                        {purgePreview.amostras.crm_contacts.map((c) => (
                          <li key={c.id} className="truncate">- <span className="font-semibold text-navy">{c.nome}</span> <span className="text-muted2">{(c.emails || []).join(", ")}</span></li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        {purgeResult && (
          <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs" data-testid="purge-result-panel">
            <div className="font-semibold text-emerald-700 mb-1">Purga concluida em {purgeResult.em?.slice(0, 19).replace("T", " ")}</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {Object.entries(purgeResult.deleted || {}).map(([k, v]) => (
                <div key={k} className="rounded-lg border border-emerald-300 bg-white p-2 text-center">
                  <div className="text-lg font-bold text-emerald-700">{v}</div>
                  <div className="text-[10px] text-muted2 leading-tight">{COL_LABELS[k] || k}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3">
        {tasks.map((t) => (
          <div key={t.key} className="border border-tint rounded-xl bg-white p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h3 className="text-navy font-bold">{t.title}</h3>
                <p className="text-xs text-muted2 mt-1 leading-relaxed">{t.desc}</p>
              </div>
              <Btn onClick={() => run(t.key, t.title)} disabled={busy === t.key} testid={t.testid}>
                {busy === t.key ? "A executar..." : "Executar"}
              </Btn>
            </div>
            {result && result.endpoint === t.key && (
              <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs">
                <div className="font-semibold text-emerald-700 mb-1">Resultado</div>
                <pre className="text-emerald-800 overflow-auto max-h-48">{JSON.stringify(result.data, null, 2)}</pre>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function BackupsTab() {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const doExport = async () => {
    setBusy(true);
    try {
      const resp = await api.get("/admin/backup/crm/export", { responseType: "blob" });
      const total = resp.headers?.["x-backup-total"];
      const cd = resp.headers?.["content-disposition"] || "";
      const match = /filename=([^;]+)/i.exec(cd);
      const fname = match ? match[1].trim() : `saashub-crm-backup-${Date.now()}.zip`;
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(`Backup descarregado (${total || "?"} registos).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro na exportacao.");
    } finally { setBusy(false); }
  };

  const doPreview = async () => {
    if (!file) return toast.error("Selecciona um ficheiro ZIP.");
    setBusy(true); setPreview(null); setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "true");
      const { data } = await api.post("/admin/backup/crm/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro na leitura do ficheiro.");
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    if (!file) return toast.error("Selecciona um ficheiro ZIP.");
    const total = Object.values(preview?.resumo || {}).reduce((a, b) => a + (b.total_no_ficheiro || 0), 0);
    if (total === 0) return toast.error("Nada a importar.");
    if (!window.confirm(`Vais restaurar ${total} registos em cascata (upsert por id). Esta operacao pode sobrepor dados existentes. Confirmas?`)) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", "false");
      const { data } = await api.post("/admin/backup/crm/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      setPreview(null);
      toast.success("Restore concluido.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro no restore.");
    } finally { setBusy(false); }
  };

  const COL_LABELS = {
    crm_accounts: "Contas",
    crm_account_address_history: "Historico de moradas",
    crm_contacts: "Contactos",
    crm_contact_history: "Historico de contactos",
    crm_activities: "Actividades",
    crm_deals: "Negocios",
    crm_meetings: "Reunioes",
    crm_tasks: "Tarefas",
    crm_pipelines: "Funis",
    crm_queues: "Filas",
    crm_teams: "Equipas",
    chat_handoffs: "Handoffs",
    chat_sessions: "Sessoes de chat",
  };

  return (
    <div className="space-y-5 max-w-4xl" data-testid="backups-tab">
      <p className="text-sm text-muted2">
        Backup completo do CRM: contas, contactos, negocios, actividades, reunioes, tarefas, historicos, funis, equipas, handoffs e sessoes.
        O ZIP inclui um <b>CSV por coleccao</b> (importavel de volta), um <b>manifest.json</b> e um <b>PDF sumario</b> legivel.
      </p>

      {/* EXPORT */}
      <div className="border border-tint rounded-xl bg-white p-5" data-testid="backup-export-card">
        <h3 className="text-navy font-bold">Exportar backup do CRM</h3>
        <p className="text-xs text-muted2 mt-1 leading-relaxed">
          Descarrega um ZIP com o estado actual de todas as coleccoes CRM. Guarda o ficheiro em local seguro (fora do servidor).
          Recomendado no minimo antes de: importar dados, corridas de purga, alteracoes em massa.
        </p>
        <div className="flex justify-end mt-3">
          <Btn onClick={doExport} disabled={busy} testid="backup-export-btn">
            {busy ? "A gerar ZIP..." : "Descarregar backup ZIP"}
          </Btn>
        </div>
      </div>

      {/* IMPORT */}
      <div className="border border-amber-200 bg-amber-50/50 rounded-xl p-5" data-testid="backup-import-card">
        <div className="flex items-start justify-between gap-4 mb-2">
          <div>
            <h3 className="text-navy font-bold flex items-center gap-2">
              <span className="text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded uppercase">critico</span>
              Importar backup / Restore
            </h3>
            <p className="text-xs text-muted2 mt-1 leading-relaxed">
              Envia um ZIP produzido pelo botao acima. Fluxo: <b>Pre-visualizar</b> (dry run - le e conta) → <b>Aplicar</b> (upsert por id: sobrepoe registos existentes e cria os novos).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="file" accept=".zip" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setImportResult(null); }}
            data-testid="backup-file-input"
            className="text-xs file:mr-2 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-blue file:text-white file:cursor-pointer" />
          {file && <span className="text-xs text-muted2">{file.name} - {(file.size / 1024).toFixed(1)} KB</span>}
        </div>
        <div className="flex justify-end gap-2 mt-3">
          <Btn variant="ghost" onClick={doPreview} disabled={busy || !file} testid="backup-preview-btn">
            {busy && !preview ? "A analisar..." : "Pre-visualizar"}
          </Btn>
          <Btn onClick={doImport} disabled={busy || !preview} testid="backup-import-btn"
               className="bg-red-600 hover:bg-red-700">
            {busy && preview ? "A aplicar..." : "Aplicar restore"}
          </Btn>
        </div>

        {preview && (
          <div className="mt-3 space-y-2" data-testid="backup-preview-panel">
            <div className="text-xs text-muted2">
              Manifest: versao {preview.manifest?.versao || "?"} - exportado em {preview.manifest?.exportado_em?.slice(0, 19).replace("T", " ") || "?"}
            </div>
            <div className="border border-tint rounded-lg overflow-hidden bg-white">
              <table className="w-full text-xs">
                <thead className="bg-tint text-navy">
                  <tr>
                    <th className="text-left px-2 py-1.5">Coleccao</th>
                    <th className="text-right px-2 py-1.5">No ficheiro</th>
                    <th className="text-right px-2 py-1.5">Ja existem (upsert)</th>
                    <th className="text-right px-2 py-1.5">Novos</th>
                    <th className="text-right px-2 py-1.5">Sem id</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(preview.resumo || {}).map(([k, v]) => (
                    <tr key={k} className={`border-t border-tint ${v.total_no_ficheiro === 0 ? "opacity-50" : ""}`}>
                      <td className="px-2 py-1.5">{COL_LABELS[k] || k}</td>
                      <td className="text-right px-2 py-1.5 font-mono">{v.total_no_ficheiro}</td>
                      <td className="text-right px-2 py-1.5 font-mono text-amber-700">{v.existentes_upsert}</td>
                      <td className="text-right px-2 py-1.5 font-mono text-emerald-700">{v.novos}</td>
                      <td className="text-right px-2 py-1.5 font-mono text-slate-400">{v.sem_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-muted2">Registos com id existentes serao <b>sobrepostos</b> pelos dados do backup. Registos novos serao inseridos.</div>
          </div>
        )}

        {importResult && (
          <div className="mt-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs" data-testid="backup-result-panel">
            <div className="font-semibold text-emerald-700 mb-1">Restore concluido em {importResult.em?.slice(0, 19).replace("T", " ")}</div>
            <div className="border border-emerald-300 rounded-lg overflow-hidden bg-white">
              <table className="w-full text-xs">
                <thead className="bg-emerald-100 text-emerald-800">
                  <tr>
                    <th className="text-left px-2 py-1.5">Coleccao</th>
                    <th className="text-right px-2 py-1.5">Inseridos</th>
                    <th className="text-right px-2 py-1.5">Actualizados</th>
                    <th className="text-right px-2 py-1.5">Saltados</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(importResult.aplicado || {}).map(([k, v]) => (
                    <tr key={k} className="border-t border-emerald-200">
                      <td className="px-2 py-1.5">{COL_LABELS[k] || k}</td>
                      <td className="text-right px-2 py-1.5 font-mono text-emerald-700">{v.inserted}</td>
                      <td className="text-right px-2 py-1.5 font-mono text-blue">{v.updated}</td>
                      <td className="text-right px-2 py-1.5 font-mono text-slate-400">{v.skipped}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function PlataformaCredsModal({ plat, onClose, onSaved }) {
  const [c, setC] = useState({ api_key: "", api_secret: "", webhook_secret: "" });
  const save = async () => {
    try {
      await api.put(`/platforms/${plat.id}/settings`, c);
      toast.success("Credenciais guardadas.");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title={`Credenciais - ${plat.nome}`} testid="plat-creds-modal">
      <p className="text-xs text-muted2 mb-2">Os segredos sao guardados encriptados. Preencha apenas os campos que quer atualizar. Deixe em branco para manter o valor atual.</p>
      <Field label="API Key" type="password" value={c.api_key} onChange={(v) => setC({ ...c, api_key: v })} testid="plat-cred-api-key" />
      <Field label="API Secret" type="password" value={c.api_secret} onChange={(v) => setC({ ...c, api_secret: v })} testid="plat-cred-api-secret" />
      <Field label="Webhook Secret" type="password" value={c.webhook_secret} onChange={(v) => setC({ ...c, webhook_secret: v })} testid="plat-cred-webhook" />
      <div className="text-[11px] text-muted2 pt-1">
        Estado atual: {plat.api_key_configurado ? "API Key OK" : "sem API key"}{plat.webhook_secret_configurado ? " - webhook OK" : ""}.
      </div>
      <div className="flex justify-end pt-3"><Btn onClick={save} testid="plat-creds-save">Guardar credenciais</Btn></div>
    </Modal>
  );
}

function RegrasTicketsTab() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/tickets/rules").then(({ data }) => setRows(data || [])).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  const empty = { nome: "", ativo: true, prioridade_ordem: 100, cliente_id: "", cliente_regex: "", produto: "", prioridade_alvo: "", origem_alvo: "", owner_email: "" };
  const remove = async (r) => {
    if (!window.confirm(`Eliminar regra "${r.nome}"?`)) return;
    try { await api.delete(`/tickets/rules/${r.id}`); toast.success("Regra eliminada."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="ticket-rules-settings">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted2 max-w-2xl">Regras avaliadas por ordem crescente de prioridade quando um novo ticket e criado. A primeira regra que corresponde atribui automaticamente o ticket ao responsavel indicado. Pode combinar filtros de cliente, produto/assunto, prioridade e origem.</p>
        <Btn onClick={() => setEdit({ ...empty })} testid="rule-new"><Plus size={15} /> Nova regra</Btn>
      </div>
      <Table
        columns={[
          { key: "prioridade_ordem", label: "Ordem" },
          { key: "nome", label: "Nome", render: (r) => <span className="font-semibold text-navy">{r.nome}</span> },
          { key: "cliente_id", label: "Cliente", render: (r) => r.cliente_id || r.cliente_regex || "-" },
          { key: "produto", label: "Produto/assunto", render: (r) => r.produto || "-" },
          { key: "prioridade_alvo", label: "Prioridade", render: (r) => r.prioridade_alvo || "qq" },
          { key: "owner_email", label: "Atribui a", render: (r) => <code className="text-xs bg-tint px-1.5 py-0.5 rounded">{r.owner_email}</code> },
          { key: "ativo", label: "Ativa", render: (r) => <Badge value={r.ativo ? "ativa" : "inativa"} /> },
          { key: "acoes", label: "Acoes", render: (r) => (
            <div className="flex gap-1">
              <button onClick={() => setEdit({ ...r })} data-testid={`rule-edit-${r.id}`} className="text-xs px-2 py-1 rounded bg-tint text-navy hover:bg-blue hover:text-white transition-colors">Editar</button>
              <button onClick={() => remove(r)} data-testid={`rule-delete-${r.id}`} className="text-xs px-2 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">Eliminar</button>
            </div>
          ) },
        ]}
        rows={rows}
        testid="rules-table"
      />
      {edit && <RegraModal value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function RegraModal({ value, onClose, onSaved }) {
  const [r, setR] = useState(value);
  const isEdit = !!value.id;
  const save = async () => {
    if (!r.nome || !r.owner_email) return toast.error("Nome e email do responsavel sao obrigatorios.");
    try {
      const body = { ...r, prioridade_ordem: parseInt(r.prioridade_ordem, 10) || 100 };
      delete body.id; delete body.criado_em; delete body.atualizado_em;
      if (isEdit) await api.put(`/tickets/rules/${value.id}`, body); else await api.post("/tickets/rules", body);
      toast.success(isEdit ? "Regra atualizada." : "Regra criada."); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Modal open onClose={onClose} title={isEdit ? `Editar regra` : "Nova regra"} testid="rule-modal">
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Nome da regra" value={r.nome} onChange={(v) => setR({ ...r, nome: v })} testid="rule-nome" />
        <Field label="Ordem (menor = mais prioritaria)" type="number" value={r.prioridade_ordem} onChange={(v) => setR({ ...r, prioridade_ordem: v })} testid="rule-ordem" />
      </div>
      <p className="text-xs font-semibold text-navy mt-2">Filtros (tudo AND, deixe vazio para ignorar)</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Cliente contem" value={r.cliente_id || ""} onChange={(v) => setR({ ...r, cliente_id: v })} testid="rule-cliente" />
        <Field label="Cliente regex (avancado)" value={r.cliente_regex || ""} onChange={(v) => setR({ ...r, cliente_regex: v })} testid="rule-cliente-regex" />
      </div>
      <Field label="Produto/assunto (regex sobre assunto ou descricao)" value={r.produto || ""} onChange={(v) => setR({ ...r, produto: v })} testid="rule-produto" />
      <div className="grid md:grid-cols-2 gap-3">
        <Selecter label="Prioridade alvo" value={r.prioridade_alvo || ""} onChange={(v) => setR({ ...r, prioridade_alvo: v })} testid="rule-prio" options={[{ value: "", label: "Qualquer" }, { value: "alta", label: "Alta" }, { value: "media", label: "Media" }, { value: "baixa", label: "Baixa" }]} />
        <Selecter label="Origem alvo" value={r.origem_alvo || ""} onChange={(v) => setR({ ...r, origem_alvo: v })} testid="rule-origem" options={[{ value: "", label: "Qualquer" }, { value: "cliente", label: "Cliente" }, { value: "sistema", label: "Sistema" }, { value: "email", label: "Email" }, { value: "chamada", label: "Chamada" }, { value: "portal", label: "Portal" }]} />
      </div>
      <p className="text-xs font-semibold text-navy mt-2">Acao</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Atribuir a (email do utilizador)" value={r.owner_email || ""} onChange={(v) => setR({ ...r, owner_email: v })} testid="rule-owner" />
        <Selecter label="Ativa" value={r.ativo ? "1" : "0"} onChange={(v) => setR({ ...r, ativo: v === "1" })} testid="rule-ativa" options={[{ value: "1", label: "Sim" }, { value: "0", label: "Nao" }]} />
      </div>
      <div className="flex justify-end pt-3"><Btn onClick={save} testid="rule-save">Guardar regra</Btn></div>
    </Modal>
  );
}

function MetasTab() {
  const [m, setM] = useState({ mrr_mensal: 0, vendas_mensal: 0 });
  const load = async () => { try { const { data } = await api.get("/dashboard/metas-config"); setM({ mrr_mensal: data.mrr_mensal || 0, vendas_mensal: data.vendas_mensal || 0 }); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async () => {
    try { await api.put("/dashboard/metas-config", { mrr_mensal: parseFloat(m.mrr_mensal) || 0, vendas_mensal: parseFloat(m.vendas_mensal) || 0 }); toast.success("Metas guardadas."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-3xl space-y-5" data-testid="metas-settings">
      <p className="text-sm text-muted2">Defina as metas globais mensais. As metas individuais por comercial definem-se em CRM &gt; Equipa (editar membro). O progresso e mostrado no Dashboard (cartao Metas comerciais). Coloque 0 para nao definir meta.</p>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Meta de MRR (EUR)" type="number" value={m.mrr_mensal} onChange={(v) => setM({ ...m, mrr_mensal: v })} testid="meta-mrr" />
        <Field label="Meta de vendas do mes (EUR)" type="number" value={m.vendas_mensal} onChange={(v) => setM({ ...m, vendas_mensal: v })} testid="meta-vendas" />
      </div>
      <Btn onClick={save} testid="metas-save">Guardar metas</Btn>
    </div>
  );
}

function CreditTab() {
  const [limite, setLimite] = useState(0);
  const load = async () => { try { const { data } = await api.get("/crm/credit-settings"); setLimite(data.limite_global || 0); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async () => {
    try { await api.put("/crm/credit-settings", { limite_global: parseFloat(limite) || 0 }); toast.success("Limite de credito guardado."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="max-w-3xl space-y-5" data-testid="credit-settings">
      <p className="text-sm text-muted2">Limite global de credito (divida em aberto) por cliente. Ao ultrapassar, e mostrado um aviso na ficha da conta e no formulario de venda. Pode definir um limite especifico por conta na ficha do cliente (que tem prioridade). Coloque 0 para desligar o limite global.</p>
      <div className="max-w-xs"><Field label="Limite global (EUR)" type="number" value={limite} onChange={setLimite} testid="credit-limit" /></div>
      <Btn onClick={save} testid="credit-save">Guardar</Btn>
    </div>
  );
}

function ProdutosTab() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [p, setP] = useState({ nome: "", sku: "", categoria: "geral", preco_base: 0, iva_pct: 23 });

  const load = async () => { try { const { data } = await api.get("/cpq/products"); setRows(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    if (!p.nome.trim()) return toast.error("Indique o nome.");
    try { await api.post("/cpq/products", { ...p, preco_base: parseFloat(p.preco_base) || 0, iva_pct: parseFloat(p.iva_pct) || 0, tiers: [] }); toast.success("Produto criado."); setShow(false); setP({ nome: "", sku: "", categoria: "geral", preco_base: 0, iva_pct: 23 }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/cpq/products/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div data-testid="produtos-tab">
      <div className="flex justify-end mb-3"><Btn onClick={() => setShow(true)} testid="new-product-btn"><Plus size={16} /> Novo produto</Btn></div>
      <Table testid="products-table" columns={[
        { key: "nome", label: "Nome", render: (r) => <span className="font-semibold text-navy">{r.nome}</span> },
        { key: "sku", label: "SKU", render: (r) => <code className="text-xs">{r.sku || "-"}</code> },
        { key: "categoria", label: "Categoria" },
        { key: "preco_base", label: "Preco base", render: (r) => `${(r.preco_base || 0).toFixed(2)} EUR` },
        { key: "iva_pct", label: "IVA%" },
        { key: "acoes", label: "", render: (r) => <button onClick={() => remove(r.id)} data-testid={`del-prod-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={15} /></button> },
      ]} rows={rows} />
      <Modal open={show} onClose={() => setShow(false)} title="Novo produto" testid="product-modal">
        <Field label="Nome" value={p.nome} onChange={(v) => setP({ ...p, nome: v })} testid="prod-nome" />
        <Field label="SKU" value={p.sku} onChange={(v) => setP({ ...p, sku: v })} testid="prod-sku" />
        <Field label="Categoria" value={p.categoria} onChange={(v) => setP({ ...p, categoria: v })} testid="prod-categoria" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Preco base" type="number" value={p.preco_base} onChange={(v) => setP({ ...p, preco_base: v })} testid="prod-preco" />
          <Field label="IVA %" type="number" value={p.iva_pct} onChange={(v) => setP({ ...p, iva_pct: v })} testid="prod-iva" />
        </div>
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancelar</Btn><Btn onClick={create} testid="prod-save">Criar</Btn></div>
      </Modal>
    </div>
  );
}

function SendGridSetupBox({ s }) {
  const eventUrl = s?.event_webhook_url || "";
  const inboundUrl = s?.inbound_parse_url || "";
  const copy = async (v, label) => {
    try { await navigator.clipboard.writeText(v); toast.success(`${label} copiado.`); }
    catch { toast.message("Nao foi possivel copiar automaticamente. Selecione manualmente."); }
  };
  const Row = ({ tag, label, url, hint, testid }) => (
    <div className="rounded-lg border border-tint bg-tint/40 p-3 space-y-1" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <Badge value={tag} />
          <span className="ml-2 text-sm font-semibold text-navy">{label}</span>
        </div>
        <button type="button" onClick={() => copy(url, label)} data-testid={`${testid}-copy`} className="text-xs px-2 py-1 rounded bg-blue text-white hover:bg-blue/90 transition-colors">Copiar URL</button>
      </div>
      <code className="block w-full break-all text-[11px] bg-white border border-tint rounded px-2 py-1 text-navy" data-testid={`${testid}-url`}>{url || "(defina PUBLIC_BACKEND_URL para gerar URL absoluto)"}</code>
      <p className="text-[11px] text-muted2 leading-relaxed">{hint}</p>
    </div>
  );
  return (
    <div className="border border-tint rounded-xl p-4 space-y-3" data-testid="sendgrid-setup">
      <h3 className="text-navy font-bold tracking-tight">Setup no painel SendGrid</h3>
      <p className="text-xs text-muted2 leading-relaxed">
        Passo 1: Crie uma <b>Single Sender</b> ou domain authentication com o email remetente acima. Passo 2: Va a <b>Settings -&gt; Mail Settings -&gt; Event Webhook</b> e cole o URL "Event Webhook" abaixo, active os eventos <i>Delivered, Opened, Clicked, Bounce, Dropped, Spam Report</i> e ative a assinatura ECDSA (copie a chave publica gerada para o campo acima "Chave publica do Event Webhook"). Passo 3: Se quiser receber emails no CRM, active o <b>Inbound Parse</b> em <b>Settings -&gt; Inbound Parse</b> apontando o seu subdominio para o URL "Inbound Parse".
      </p>
      <Row tag="OPEN/CLICK" label="Event Webhook URL" url={eventUrl} testid="sg-setup-event"
        hint="Recebe os eventos de entrega, abertura e cliques dos emails enviados. Requer assinatura ECDSA activa." />
      <Row tag="INBOUND" label="Inbound Parse URL" url={inboundUrl} testid="sg-setup-inbound"
        hint="Recebe emails enviados para o seu dominio (ex.: suporte@cogniseg.net) e cria leads/associa a conta CRM automaticamente." />
      {!s?.public_backend_url && (
        <div className="rounded bg-amber-50 border border-amber-200 text-amber-800 text-xs p-2">
          Aviso: nao foi possivel derivar o dominio publico. Defina <code>PUBLIC_BACKEND_URL</code> nas variaveis de ambiente do backend para obter URLs absolutas.
        </div>
      )}
    </div>
  );
}

function EmailTab() {
  const [s, setS] = useState({ configured: false, from_email: "", from_name: "", api_key_masked: "", webhook_configured: false });
  const [apiKey, setApiKey] = useState("");
  const [pubKey, setPubKey] = useState("");
  const [testTo, setTestTo] = useState("");
  const [log, setLog] = useState({ rows: [], total: 0, skip: 0, limit: 50 });
  const [events, setEvents] = useState({ rows: [], total: 0, skip: 0, limit: 50 });
  const [logPage, setLogPage] = useState(0);
  const [evPage, setEvPage] = useState(0);
  const [logQ, setLogQ] = useState("");
  const [logEstado, setLogEstado] = useState("todos");
  const [evQ, setEvQ] = useState("");
  const [evEvent, setEvEvent] = useState("todos");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const PAGE = 50;

  const loadConfig = async () => {
    try { const { data } = await api.get("/email/settings"); setS(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const loadLog = async (page = 0, q = logQ, estado = logEstado) => {
    try {
      const { data } = await api.get("/email/log", { params: { skip: page * PAGE, limit: PAGE, q, estado } });
      setLog(data); setLogPage(page);
    } catch (e) { /* noop */ }
  };
  const loadEvents = async (page = 0, q = evQ, event = evEvent) => {
    try {
      const { data } = await api.get("/email/events", { params: { skip: page * PAGE, limit: PAGE, q, event } });
      setEvents(data); setEvPage(page);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { loadConfig(); loadLog(0); loadEvents(0); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/email/settings", { api_key: apiKey || undefined, from_email: s.from_email, from_name: s.from_name, webhook_public_key: pubKey || undefined });
      toast.success("Definicoes guardadas."); setApiKey(""); setPubKey(""); loadConfig();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const test = async () => {
    if (!testTo) return toast.error("Indique um email de destino.");
    setTesting(true);
    try {
      const { data } = await api.post("/email/test", { para: testTo });
      toast[data.estado === "enviado" ? "success" : "message"](`Estado: ${data.estado}${data.erro ? " - " + data.erro : ""}`);
      loadLog(0);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const badge = s.configured
    ? <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700" data-testid="email-status-badge">Ativo{s.api_key_masked ? ` - ${s.api_key_masked}` : ""}</span>
    : <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-red-200 bg-red-50 text-red-700" data-testid="email-status-badge">Nao configurado</span>;

  return (
    <div className="space-y-5" data-testid="email-settings">
      <SettingsHero
        iconLabel="SG"
        title="Email (SendGrid)"
        description="Configuracao do remetente, chave API SendGrid e webhook de eventos (delivered, open, click, bounce). Todos os envios do Hub (transacionais e marketing) usam este canal."
        badge={badge}
        actions={<Btn onClick={save} disabled={saving} testid="sg-save">{saving ? "A guardar..." : "Guardar"}</Btn>}
      />

      <SectionCard title="Remetente e credenciais" hint="Chave API e endereco a partir do qual os emails sao enviados.">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Email remetente" value={s.from_email} onChange={(v) => setS({ ...s, from_email: v })} placeholder="no-reply@cogniseg.net" testid="sg-from-email" />
          <Field label="Nome remetente" value={s.from_name} onChange={(v) => setS({ ...s, from_name: v })} placeholder="Cogniseg" testid="sg-from-name" />
          <Field label="Chave API SendGrid (deixe vazio para manter)" type="password" value={apiKey} onChange={setApiKey} placeholder="SG.xxxx" testid="sg-api-key" />
          <Field label="Chave publica Event Webhook (rastreabilidade)" value={pubKey} onChange={setPubKey} placeholder="MFkwEwY... (deixe vazio para manter)" testid="sg-webhook-key" />
        </div>
      </SectionCard>

      <SectionCard title="Como configurar o SendGrid" hint="Passos para ativar chave API e webhook de eventos.">
        <SendGridSetupBox s={s} />
      </SectionCard>

      <SectionCard title="Enviar email de teste" hint="Valide as credenciais enviando uma mensagem real.">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Field label="Destino" value={testTo} onChange={setTestTo} placeholder="eu@empresa.pt" testid="sg-test-to" />
          </div>
          <Btn variant="ghost" onClick={test} disabled={testing || !testTo} testid="sg-test-btn">
            <Send size={15} /> {testing ? "A enviar..." : "Enviar teste"}
          </Btn>
        </div>
      </SectionCard>

      <SectionCard
        title="Registo de envios"
        hint={`${log.total} envios registados. A mostrar ${log.rows.length ? (log.skip + 1) : 0}-${log.skip + log.rows.length}.`}
        action={<Pager page={logPage} total={log.total} pageSize={PAGE} onChange={loadLog} testid="log-pager" />}
        testid="email-log-section">
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={logQ} onChange={(e) => setLogQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadLog(0)}
            data-testid="log-search" placeholder="Pesquisar email ou assunto..."
            className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-tint rounded" />
          <select value={logEstado} onChange={(e) => { setLogEstado(e.target.value); loadLog(0, logQ, e.target.value); }}
            data-testid="log-filter-estado" className="px-3 py-1.5 text-sm border border-tint rounded bg-white">
            <option value="todos">Todos os estados</option>
            <option value="enviado">Enviado</option>
            <option value="falhou">Falhou</option>
            <option value="entregue">Entregue</option>
            <option value="bounce">Bounce</option>
            <option value="dropped">Descartado</option>
          </select>
          <Btn variant="ghost" onClick={() => loadLog(0)} testid="log-search-btn">Filtrar</Btn>
          {(logQ || logEstado !== "todos") && (
            <Btn variant="ghost" onClick={() => { setLogQ(""); setLogEstado("todos"); loadLog(0, "", "todos"); }} testid="log-clear">Limpar</Btn>
          )}
        </div>
        <Table testid="email-log-table" paginate={false} columns={[
          { key: "para", label: "Para" }, { key: "assunto", label: "Assunto" },
          { key: "estado", label: "Estado", render: (r) => <Badge value={r.estado} /> },
          { key: "em", label: "Quando", render: (r) => dt(r.em) },
        ]} rows={log.rows} />
      </SectionCard>

      <SectionCard
        title="Eventos SendGrid"
        hint={`Entregas, aberturas e cliques. Total: ${events.total}. A mostrar ${events.rows.length ? (events.skip + 1) : 0}-${events.skip + events.rows.length}.`}
        action={<Pager page={evPage} total={events.total} pageSize={PAGE} onChange={loadEvents} testid="events-pager" />}
        testid="email-events-section">
        <div className="flex flex-wrap gap-2 mb-3">
          <input value={evQ} onChange={(e) => setEvQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadEvents(0)}
            data-testid="events-search" placeholder="Pesquisar email..."
            className="flex-1 min-w-[200px] px-3 py-1.5 text-sm border border-tint rounded" />
          <select value={evEvent} onChange={(e) => { setEvEvent(e.target.value); loadEvents(0, evQ, e.target.value); }}
            data-testid="events-filter-event" className="px-3 py-1.5 text-sm border border-tint rounded bg-white">
            <option value="todos">Todos os eventos</option>
            <option value="delivered">Delivered</option>
            <option value="open">Open</option>
            <option value="click">Click</option>
            <option value="bounce">Bounce</option>
            <option value="dropped">Dropped</option>
            <option value="spamreport">Spam report</option>
          </select>
          <Btn variant="ghost" onClick={() => loadEvents(0)} testid="events-search-btn">Filtrar</Btn>
          {(evQ || evEvent !== "todos") && (
            <Btn variant="ghost" onClick={() => { setEvQ(""); setEvEvent("todos"); loadEvents(0, "", "todos"); }} testid="events-clear">Limpar</Btn>
          )}
        </div>
        <Table testid="email-events-table" paginate={false} columns={[
          { key: "event", label: "Evento" },
          { key: "email", label: "Email" },
          { key: "received_at", label: "Recebido", render: (r) => dt(r.received_at) },
        ]} rows={events.rows} />
      </SectionCard>

      <StickyActionsBar hint="Guarde antes de sair.">
        <Btn onClick={save} disabled={saving} testid="sg-save-bottom">{saving ? "A guardar..." : "Guardar definicoes"}</Btn>
      </StickyActionsBar>
    </div>
  );
}

function Pager({ page, total, pageSize, onChange, testid }) {
  const maxPage = Math.max(0, Math.ceil((total || 0) / pageSize) - 1);
  const canPrev = page > 0;
  const canNext = page < maxPage;
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      <button onClick={() => canPrev && onChange(page - 1)} disabled={!canPrev}
        data-testid={`${testid}-prev`}
        className={`text-[11px] px-2 py-1 rounded border ${canPrev ? "border-tint text-navy hover:bg-slate-100" : "border-tint text-slate-300 cursor-not-allowed"}`}>
        Anteriores
      </button>
      <span className="text-[11px] text-slate-500 px-1.5">
        {maxPage === 0 ? "1" : `${page + 1} / ${maxPage + 1}`}
      </span>
      <button onClick={() => canNext && onChange(page + 1)} disabled={!canNext}
        data-testid={`${testid}-next`}
        className={`text-[11px] px-2 py-1 rounded border ${canNext ? "border-tint text-navy hover:bg-slate-100" : "border-tint text-slate-300 cursor-not-allowed"}`}>
        Seguintes
      </button>
    </div>
  );
}

const TICKET_ESTADOS = ["aberto", "em_curso", "a_aguardar_cliente", "resolvido", "fechado"];
const VARIAVEIS_BASE = ["nome", "empresa", "email"];
const VARIAVEIS_TICKET = ["ticket_assunto", "ticket_estado"];
const EMPTY_TPL = { chave: "", nome: "", categoria: "geral", assunto: "", html: "", tipo: "transacional", contexto: "crm", produto_id: "", estado_alvo: "" };

function TemplatesTab() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [t, setT] = useState(EMPTY_TPL);
  const [editId, setEditId] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [stages, setStages] = useState([]);
  const htmlRef = useRef(null);

  const insertVar = (v) => {
    const el = htmlRef.current;
    const token = `{{${v}}}`;
    const cur = t.html || "";
    if (!el) { setT((s) => ({ ...s, html: cur + token })); return; }
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? start;
    const next = cur.slice(0, start) + token + cur.slice(end);
    setT((s) => ({ ...s, html: next }));
    requestAnimationFrame(() => { el.focus(); const p = start + token.length; try { el.setSelectionRange(p, p); } catch (e) { /* noop */ } });
  };

  const load = async () => { try { const { data } = await api.get("/email/templates"); setRows(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => {
    load();
    api.get("/cpq/products").then(({ data }) => setProdutos(data || [])).catch(() => {});
    api.get("/crm/deals/stages").then(({ data }) => setStages(data.stages || [])).catch(() => {});
  }, []); // eslint-disable-line

  const openNew = () => { setEditId(null); setT(EMPTY_TPL); setShow(true); };
  const openEdit = (r) => { setEditId(r.id); setT({ ...EMPTY_TPL, ...r }); setShow(true); };
  const save = async () => {
    if (!t.chave.trim() || !t.assunto.trim()) return toast.error("Chave e assunto obrigatorios.");
    try {
      if (editId) { await api.put(`/email/templates/${editId}`, t); toast.success("Template atualizado."); }
      else { await api.post("/email/templates", t); toast.success("Template criado."); }
      setShow(false); setT(EMPTY_TPL); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/email/templates/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const prodNome = (pid) => (produtos.find((p) => p.id === pid) || {}).nome || "";
  const estadoOpts = t.contexto === "servicedesk" ? TICKET_ESTADOS : stages;

  return (
    <div data-testid="templates-tab">
      <div className="flex justify-end mb-3"><Btn onClick={openNew} testid="new-template-btn"><Plus size={16} /> Novo template</Btn></div>
      <Table testid="templates-table" columns={[
        { key: "nome", label: "Nome", render: (r) => <span className="font-semibold text-navy">{r.nome || r.chave}</span> },
        { key: "contexto", label: "Contexto", render: (r) => <Badge value={(r.contexto || "crm") === "servicedesk" ? "servicedesk" : "crm"} /> },
        { key: "produto_id", label: "Produto", render: (r) => prodNome(r.produto_id) || "-" },
        { key: "estado_alvo", label: "Estado alvo", render: (r) => (r.estado_alvo || "-").replace(/_/g, " ") },
        { key: "assunto", label: "Assunto" },
        { key: "acoes", label: "", render: (r) => (
          <div className="flex gap-1">
            <button onClick={() => openEdit(r)} data-testid={`edit-tpl-${r.id}`} className="text-blue hover:bg-tint rounded p-1 text-xs font-semibold">Editar</button>
            <button onClick={() => remove(r.id)} data-testid={`del-tpl-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={15} /></button>
          </div>
        ) },
      ]} rows={rows} />
      <Modal open={show} onClose={() => setShow(false)} title={editId ? "Editar template" : "Novo template de email"} testid="template-modal">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Chave (identificador)" value={t.chave} onChange={(v) => setT({ ...t, chave: v })} placeholder="lead_boasvindas" testid="tpl-chave" />
          <Field label="Nome" value={t.nome} onChange={(v) => setT({ ...t, nome: v })} testid="tpl-nome" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Selecter label="Contexto" value={t.contexto} onChange={(v) => setT({ ...t, contexto: v, estado_alvo: "" })} testid="tpl-contexto"
            options={[{ value: "crm", label: "Email CRM" }, { value: "servicedesk", label: "Resposta de Ticket" }]} />
          <Selecter label="Produto (opcional)" value={t.produto_id} onChange={(v) => setT({ ...t, produto_id: v })} testid="tpl-produto"
            options={[{ value: "", label: "-- Nenhum --" }, ...produtos.map((p) => ({ value: p.id, label: p.nome }))]} />
          <Selecter label="Estado alvo (opcional)" value={t.estado_alvo} onChange={(v) => setT({ ...t, estado_alvo: v })} testid="tpl-estado"
            options={[{ value: "", label: "-- Qualquer --" }, ...estadoOpts.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))]} />
        </div>
        <Field label="Assunto (aceita variaveis)" value={t.assunto} onChange={(v) => setT({ ...t, assunto: v })} testid="tpl-assunto" />
        <div>
          <label className="text-sm font-semibold text-navy block mb-1">{"HTML (aceita {{variaveis}})"}</label>
          <div className="flex flex-wrap gap-1.5 mb-2" data-testid="tpl-variaveis">
            {VARIAVEIS_BASE.concat(t.contexto === "servicedesk" ? VARIAVEIS_TICKET : []).map((v) => (
              <button key={v} type="button" data-testid={`tpl-var-${v}`} onClick={() => insertVar(v)}
                className="text-[11px] font-mono bg-tint text-navy rounded px-2 py-1 hover:bg-blue hover:text-white transition-colors">{`+{{${v}}}`}</button>
            ))}
          </div>
          <textarea ref={htmlRef} value={t.html} onChange={(e) => setT({ ...t, html: e.target.value })} data-testid="tpl-html" rows={6}
            className="w-full px-3 py-2 border border-tint rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue" />
        </div>
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancelar</Btn><Btn onClick={save} testid="tpl-save">{editId ? "Guardar" : "Criar"}</Btn></div>
      </Modal>
    </div>
  );
}

function AssinaturaDigitalTab() {
  const [configs, setConfigs] = useState([]);
  const [selected, setSelected] = useState("docusign");
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/signature-providers/config"); setConfigs(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const cur = configs.find((c) => c.provider === selected) || {};

  useEffect(() => {
    setForm({
      environment: cur.environment || (selected === "docusign" ? "demo" : "production"),
      webhook_url: cur.webhook_url || "",
      lembrete_dias: cur.lembrete_dias ?? 3,
      lembrete_max: cur.lembrete_max ?? 3,
      integration_key: cur.integration_key || "",
      user_id: cur.user_id || "",
      account_id: cur.account_id || "",
      base_uri: cur.base_uri || "",
      secret_key: "",
      rsa_private_key_pem: "",
      connect_hmac_secret: "",
      api_key: "",
      client_id: cur.client_id || "",
      oauth_client_secret: "",
      test_mode: cur.test_mode ?? true,
    });
  }, [selected, configs.length]); // eslint-disable-line

  const save = async () => {
    setLoading(true);
    try {
      const body = { provider: selected, ...form };
      // Do not send empty secret strings (keeps existing encrypted values)
      Object.keys(body).forEach((k) => { if (body[k] === "") delete body[k]; });
      await api.put("/signature-providers/config", body);
      toast.success("Configuracao guardada."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const setAtivo = async () => {
    try {
      await api.put("/signature-providers/config", { provider: selected, ativo: true });
      toast.success("Provider marcado como ativo."); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const testar = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/signature-providers/test", { provider: selected });
      toast.success(`Ligacao OK: ${data.base_uri || data.account || "verificado"}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="max-w-4xl space-y-4" data-testid="assinatura-digital-tab">
      <p className="text-sm text-muted2">
        Configure plataformas de assinatura digital. Ao adjudicar uma encomenda com contrato do tipo "standard",
        o contrato e enviado automaticamente para o provider ativo. Contratos "personalizados" ficam para revisao manual.
      </p>

      <div className="flex items-center gap-3">
        <label className="text-sm font-semibold text-navy">Provider:</label>
        {["docusign", "dropbox_sign"].map((p) => {
          const c = configs.find((x) => x.provider === p) || {};
          return (
            <button key={p} onClick={() => setSelected(p)} data-testid={`sig-provider-tab-${p}`}
              className={`px-3 py-1.5 rounded text-sm border ${selected === p ? "bg-navy text-white border-navy" : "bg-white text-navy border-tint"}`}>
              {p === "docusign" ? "DocuSign" : "Dropbox Sign"}
              {c.configurado && <span className="ml-2 text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded">OK</span>}
              {c.ativo && <span className="ml-1 text-[10px] bg-blue text-white px-1.5 py-0.5 rounded">Ativo</span>}
            </button>
          );
        })}
      </div>

      <div className="bg-tint/40 rounded-lg p-4 space-y-3">
        <div className="flex gap-3">
          <Selecter label="Ambiente" value={form.environment} onChange={(v) => set("environment", v)}
            options={selected === "docusign"
              ? [{ value: "demo", label: "Demo (developer)" }, { value: "production", label: "Producao" }]
              : [{ value: "production", label: "Producao" }, { value: "demo", label: "Demo/Sandbox" }]}
            testid="sig-env" />
          <Field label="Webhook URL (calback do provider)" value={form.webhook_url}
            onChange={(v) => set("webhook_url", v)} testid="sig-webhook-url" />
        </div>

        {selected === "docusign" && (
          <>
            <Field label="Integration Key (client id)" value={form.integration_key}
              onChange={(v) => set("integration_key", v)} testid="sig-ds-ikey" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="User ID (impersonated user)" value={form.user_id}
                onChange={(v) => set("user_id", v)} testid="sig-ds-uid" />
              <Field label="Account ID" value={form.account_id}
                onChange={(v) => set("account_id", v)} testid="sig-ds-aid" />
            </div>
            <Field label="Base URI (opcional, auto-descoberto)" value={form.base_uri}
              onChange={(v) => set("base_uri", v)} testid="sig-ds-base" />
            <Field label={`Secret Key (client secret) ${cur.secret_key_mask ? " [atual: " + cur.secret_key_mask + "]" : ""}`}
              value={form.secret_key} onChange={(v) => set("secret_key", v)}
              type="password" testid="sig-ds-secret" />
            <div>
              <label className="block text-sm font-semibold text-navy mb-1">
                RSA Private Key (PEM){cur.rsa_private_key_pem_mask ? ` [atual: ${cur.rsa_private_key_pem_mask}]` : ""}
              </label>
              <textarea value={form.rsa_private_key_pem}
                onChange={(e) => set("rsa_private_key_pem", e.target.value)}
                rows={5} data-testid="sig-ds-rsa"
                placeholder="-----BEGIN PRIVATE KEY----- ... -----END PRIVATE KEY-----"
                className="w-full px-3 py-2 border border-tint rounded font-mono text-xs" />
            </div>
            <Field label={`Connect HMAC Secret ${cur.connect_hmac_secret_mask ? " [atual: " + cur.connect_hmac_secret_mask + "]" : ""}`}
              value={form.connect_hmac_secret} onChange={(v) => set("connect_hmac_secret", v)}
              type="password" testid="sig-ds-hmac" />
            <p className="text-xs text-muted2">Obtenha em: Developer Center &gt; Apps and Keys. Habilite HMAC em Admin &gt; Integrations &gt; Connect.</p>
          </>
        )}

        {selected === "dropbox_sign" && (
          <>
            <Field label={`API Key ${cur.api_key_mask ? " [atual: " + cur.api_key_mask + "]" : ""}`}
              value={form.api_key} onChange={(v) => set("api_key", v)}
              type="password" testid="sig-dbx-apikey" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Client ID (API app - opcional)" value={form.client_id}
                onChange={(v) => set("client_id", v)} testid="sig-dbx-cid" />
              <Field label={`OAuth Client Secret (opcional)`}
                value={form.oauth_client_secret} onChange={(v) => set("oauth_client_secret", v)}
                type="password" testid="sig-dbx-csec" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.test_mode}
                onChange={(e) => set("test_mode", e.target.checked)} data-testid="sig-dbx-test" />
              Test mode (documentos nao vinculativos, sem consumir plano)
            </label>
            <p className="text-xs text-muted2">Obtenha em: app.sign.dropbox.com &gt; Settings &gt; API. A API key vai como username em Basic Auth.</p>
          </>
        )}

        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-tint">
          <Field label="Lembretes automaticos: dias entre lembretes" type="number"
            value={form.lembrete_dias} onChange={(v) => set("lembrete_dias", Number(v))}
            testid="sig-lem-dias" />
          <Field label="Numero maximo de lembretes automaticos" type="number"
            value={form.lembrete_max} onChange={(v) => set("lembrete_max", Number(v))}
            testid="sig-lem-max" />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          {cur.configurado && !cur.ativo && (
            <Btn variant="ghost" onClick={setAtivo} testid="sig-set-active">Marcar como ativo</Btn>
          )}
          {cur.configurado && (
            <Btn variant="ghost" onClick={testar} testid="sig-test">
              {testing ? "A testar..." : "Testar ligacao"}
            </Btn>
          )}
          <Btn onClick={save} testid="sig-save">{loading ? "A guardar..." : "Guardar"}</Btn>
        </div>
      </div>
    </div>
  );
}

function KBTab() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [a, setA] = useState({ titulo: "", categoria: "geral", conteudo: "", anexos: [] });
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = async () => { try { const { data } = await api.get("/kb/articles"); setRows(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const openNew = () => { setEditId(null); setA({ titulo: "", categoria: "geral", conteudo: "", anexos: [] }); setShow(true); };
  const openEdit = (r) => { setEditId(r.id); setA({ titulo: r.titulo, categoria: r.categoria || "geral", conteudo: r.conteudo || "", anexos: r.anexos || [] }); setShow(true); };
  const save = async () => {
    if (!a.titulo.trim()) return toast.error("Indique o titulo.");
    try {
      const body = { titulo: a.titulo, categoria: a.categoria, conteudo: a.conteudo };
      if (editId) await api.put(`/kb/articles/${editId}`, body); else {
        const { data } = await api.post("/kb/articles", body);
        setEditId(data.id); setA((cur) => ({ ...cur, anexos: data.anexos || [] }));
      }
      toast.success("Artigo guardado."); load();
      if (editId) setShow(false);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const uploadAnexo = async (file) => {
    if (!editId) { toast.error("Grave o artigo primeiro para anexar ficheiros."); return; }
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Ficheiro > 5MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/kb/articles/${editId}/anexos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setA((cur) => ({ ...cur, anexos: [...(cur.anexos || []), data] }));
      toast.success("Anexo carregado.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const removeAnexo = async (fid) => {
    if (!editId) return;
    if (!window.confirm("Eliminar anexo?")) return;
    try {
      await api.delete(`/kb/articles/${editId}/anexos/${fid}`);
      setA((cur) => ({ ...cur, anexos: (cur.anexos || []).filter((x) => x.id !== fid) }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/kb/articles/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  return (
    <div data-testid="kb-tab">
      <p className="text-sm text-muted2 mb-3">Artigos de ajuda reutilizaveis para colar rapidamente nas respostas de tickets (Suporte). Pode anexar PDFs a cada artigo - ao inserir o artigo numa resposta, os anexos ficam disponiveis para adicionar ao email.</p>
      <div className="flex justify-end mb-3"><Btn onClick={openNew} testid="new-kb-btn"><Plus size={16} /> Novo artigo</Btn></div>
      <Table testid="kb-table" columns={[
        { key: "titulo", label: "Titulo", render: (r) => <span className="font-semibold text-navy">{r.titulo}</span> },
        { key: "categoria", label: "Categoria" },
        { key: "anexos", label: "Anexos", render: (r) => (r.anexos || []).length },
        { key: "acoes", label: "", render: (r) => (
          <div className="flex gap-1">
            <button onClick={() => openEdit(r)} data-testid={`edit-kb-${r.id}`} className="text-blue hover:bg-tint rounded p-1 text-xs font-semibold">Editar</button>
            <button onClick={() => remove(r.id)} data-testid={`del-kb-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={15} /></button>
          </div>
        ) },
      ]} rows={rows} />
      <Modal open={show} onClose={() => setShow(false)} title={editId ? "Editar artigo" : "Novo artigo"} testid="kb-modal">
        <Field label="Titulo" value={a.titulo} onChange={(v) => setA({ ...a, titulo: v })} testid="kb-titulo" />
        <Field label="Categoria" value={a.categoria} onChange={(v) => setA({ ...a, categoria: v })} testid="kb-categoria" />
        <label className="block text-sm font-semibold text-navy mb-1">Conteudo</label>
        <RichText value={a.conteudo} onChange={(v) => setA({ ...a, conteudo: v })} testid="kb-conteudo" />
        <div className="mt-3">
          <label className="block text-sm font-semibold text-navy mb-1">Anexos (PDFs / max 5MB cada)</label>
          {!editId && <p className="text-[11px] text-muted2 mb-2">Grave o artigo primeiro para poder anexar ficheiros.</p>}
          <div className="space-y-1" data-testid="kb-anexos-list">
            {(a.anexos || []).map((x) => (
              <div key={x.id} className="flex items-center gap-2 text-sm bg-tint/40 rounded px-2 py-1">
                <span className="truncate flex-1 text-navy">{x.nome}</span>
                <span className="text-[11px] text-muted2 shrink-0">{Math.round((x.tamanho || 0) / 1024)} KB</span>
                <button onClick={() => removeAnexo(x.id)} data-testid={`kb-anexo-remove-${x.id}`} className="text-red-600 hover:text-red-800"><Trash2 size={13} /></button>
              </div>
            ))}
            {(a.anexos || []).length === 0 && <div className="text-xs text-muted2">Sem anexos.</div>}
          </div>
          <label className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${editId ? "bg-blue text-white cursor-pointer hover:bg-blue/90" : "bg-tint text-muted2 cursor-not-allowed"}`}>
            <input type="file" accept="application/pdf,image/*" onChange={(e) => uploadAnexo(e.target.files?.[0])} disabled={!editId || uploading} data-testid="kb-anexo-input" className="hidden" />
            {uploading ? "A carregar..." : "Adicionar anexo"}
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-3"><Btn variant="ghost" onClick={() => setShow(false)}>Fechar</Btn><Btn onClick={save} testid="kb-save">{editId ? "Guardar" : "Criar"}</Btn></div>
      </Modal>
    </div>
  );
}

function CpqWorkflowTab() {
  const [wf, setWf] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/cpq/workflow"); setWf(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const setField = (k, v) => setWf((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/cpq/workflow", {
        requer_assinatura: !!wf.requer_assinatura,
        aceitar_gera_encomenda: !!wf.aceitar_gera_encomenda,
        encomenda_gera_contrato: !!wf.encomenda_gera_contrato,
        encomenda_gera_fatura: !!wf.encomenda_gera_fatura,
        encomenda_provisiona_tenant: !!wf.encomenda_provisiona_tenant,
        desconto_aprovacao_pct: parseFloat(wf.desconto_aprovacao_pct) || 0,
        contrato_duracao_meses: parseInt(wf.contrato_duracao_meses) || 12,
        renovacao_automatica: !!wf.renovacao_automatica,
      });
      setWf(data); toast.success("Workflow guardado.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Reset para valores por defeito?")) return;
    try { const { data } = await api.post("/cpq/workflow/reset"); setWf(data); toast.success("Reset feito."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!wf) return <div className="text-sm text-muted2">A carregar...</div>;

  const Toggle = ({ k, label, hint }) => (
    <label className="flex items-start gap-3 py-2.5 border-b border-tint last:border-b-0 cursor-pointer">
      <input type="checkbox" checked={!!wf[k]} onChange={(e) => setField(k, e.target.checked)}
        data-testid={`wf-${k}`} className="mt-0.5" />
      <div className="flex-1">
        <div className="text-sm font-semibold text-navy">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
    </label>
  );

  return (
    <div className="max-w-3xl space-y-4" data-testid="cpq-workflow-tab">
      <SectionCard title="Aceitacao da proposta" hint="Define o que acontece quando uma proposta e aceite pelo cliente ou por um comercial.">
        <Toggle k="requer_assinatura" label="Exigir assinatura antes de aceitar"
          hint="Quando activo, so propostas com estado 'assinada' podem ser aceites (excepto quando forcado via API)." />
        <Toggle k="aceitar_gera_encomenda" label="Ao aceitar proposta, criar encomenda automaticamente"
          hint="Cria uma encomenda com os mesmos itens da proposta e marca-a como adjudicada." />
      </SectionCard>

      <SectionCard title="Encomenda adjudicada" hint="Passos automaticos disparados quando a encomenda passa a adjudicada/ativa.">
        <Toggle k="encomenda_gera_contrato" label="Gerar contrato automaticamente"
          hint="Cria contrato usando o template default da plataforma (se existir)." />
        <Toggle k="encomenda_gera_fatura" label="Emitir fatura automaticamente"
          hint="Emite fatura via Moloni (se configurado) ou registo mock interno." />
        <Toggle k="encomenda_provisiona_tenant" label="Provisionar tenant no conector automaticamente"
          hint="Cria o tenant remoto no CogniEtica/Privaxia/conectores dinamicos, se o plano estiver ligado a uma plataforma." />
      </SectionCard>

      <SectionCard title="Aprovacoes e defaults">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Desconto que exige aprovacao (%)" type="number" value={wf.desconto_aprovacao_pct}
            onChange={(v) => setField("desconto_aprovacao_pct", v)} testid="wf-desconto-aprovacao" />
          <Field label="Duracao contrato default (meses)" type="number" value={wf.contrato_duracao_meses}
            onChange={(v) => setField("contrato_duracao_meses", v)} testid="wf-duracao-meses" />
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-navy pb-2">
              <input type="checkbox" checked={!!wf.renovacao_automatica}
                onChange={(e) => setField("renovacao_automatica", e.target.checked)}
                data-testid="wf-renovacao" />
              Renovacao automatica default
            </label>
          </div>
        </div>
      </SectionCard>

      <div className="flex justify-end gap-2 pt-2">
        <Btn variant="ghost" onClick={reset} testid="wf-reset">Reset defaults</Btn>
        <Btn onClick={save} testid="wf-save" disabled={saving}>{saving ? "A guardar..." : "Guardar workflow"}</Btn>
      </div>
    </div>
  );
}


function CpqRenewalTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/cpq/renewal-config"); setCfg(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/cpq/renewal-config", {
        enabled: !!cfg.enabled,
        threshold_dias: parseInt(cfg.threshold_dias) || 30,
        notify_email: !!cfg.notify_email,
        notify_inapp: !!cfg.notify_inapp,
      });
      setCfg(data); toast.success("Config guardada.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-sm text-muted2">A carregar...</div>;

  return (
    <div className="max-w-2xl space-y-4" data-testid="cpq-renewal-tab">
      <SectionCard title="Renovacao automatica de contratos"
        hint="Gera automaticamente uma proposta de renovacao com base no contrato actual, no periodo definido antes do fim.">
        <label className="flex items-start gap-3 py-2.5 border-b border-tint cursor-pointer">
          <input type="checkbox" checked={!!cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            data-testid="renewal-enabled" className="mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-navy">Activar renovacao automatica</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Job periodico (12h) verifica contratos activos e cria propostas de renovacao rascunho.</div>
          </div>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
          {[30, 60, 90].map((d) => (
            <button key={d} type="button" onClick={() => setCfg({ ...cfg, threshold_dias: d })}
              data-testid={`renewal-preset-${d}`}
              className={`p-3 rounded-lg border text-sm font-semibold transition ${parseInt(cfg.threshold_dias) === d ? "bg-navy text-white border-navy" : "bg-white text-navy border-tint hover:border-navy"}`}>
              {d} dias antes
            </button>
          ))}
        </div>
        <Field label="Dias antes do fim (personalizado)" type="number" value={cfg.threshold_dias}
          onChange={(v) => setCfg({ ...cfg, threshold_dias: v })} testid="renewal-threshold" />
        <label className="flex items-center gap-2 text-sm text-navy pt-2">
          <input type="checkbox" checked={!!cfg.notify_email}
            onChange={(e) => setCfg({ ...cfg, notify_email: e.target.checked })}
            data-testid="renewal-notify-email" />
          Notificar por email o account owner
        </label>
        <label className="flex items-center gap-2 text-sm text-navy pt-1">
          <input type="checkbox" checked={!!cfg.notify_inapp}
            onChange={(e) => setCfg({ ...cfg, notify_inapp: e.target.checked })}
            data-testid="renewal-notify-inapp" />
          Mostrar no sino de notificacoes
        </label>
      </SectionCard>
      <div className="flex justify-end">
        <Btn onClick={save} testid="renewal-save" disabled={saving}>{saving ? "A guardar..." : "Guardar"}</Btn>
      </div>
    </div>
  );
}


function CpqTemplatesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [activeEditorTab, setActiveEditorTab] = useState("campos");

  const load = async () => {
    try { const { data } = await api.get("/cpq/proposal-templates"); setRows(data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const empty = () => ({ nome: "", descricao: "", plataforma: "", ativo: true, notas: "", corpo_html: "", duracao_subscricao: 1, competitive_upgrade: false, desconto_pct_global: 0, condicoes_pagamento: "pronto", metodo_pagamento: "sepa", validade_dias: 30, itens: [{ descricao: "", quantidade: 1, preco_unitario: 0, desconto_pct: 0, iva_pct: 23, periodo: "anual" }] });

  const save = async () => {
    if (!editing.nome?.trim()) return toast.error("Nome obrigatorio.");
    try {
      const payload = {
        ...editing,
        desconto_pct_global: parseFloat(editing.desconto_pct_global) || 0,
        validade_dias: parseInt(editing.validade_dias) || 30,
        itens: (editing.itens || []).map((it) => ({
          ...it,
          quantidade: parseFloat(it.quantidade) || 0,
          preco_unitario: parseFloat(it.preco_unitario) || 0,
          desconto_pct: parseFloat(it.desconto_pct) || 0,
          iva_pct: parseFloat(it.iva_pct) || 23,
        })),
      };
      if (editing.id) await api.put(`/cpq/proposal-templates/${editing.id}`, payload);
      else await api.post("/cpq/proposal-templates", payload);
      toast.success("Template guardado."); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminar template?")) return;
    try { await api.delete(`/cpq/proposal-templates/${id}`); toast.success("Eliminado."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setItem = (i, k, v) => setEditing((s) => ({ ...s, itens: s.itens.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const addItem = () => setEditing((s) => ({ ...s, itens: [...(s.itens || []), { descricao: "", quantidade: 1, preco_unitario: 0, desconto_pct: 0, iva_pct: 23, periodo: "anual" }] }));
  const rmItem = (i) => setEditing((s) => ({ ...s, itens: s.itens.filter((_, idx) => idx !== i) }));

  const DURACOES = [{ value: 1, label: "1 ano" }, { value: 3, label: "3 anos" }, { value: 5, label: "5 anos" }];
  const VARS = ["nome_cliente", "nif_cliente", "data_proposta", "numero_proposta", "total", "validade", "comercial_nome", "empresa_nome"];

  return (
    <div data-testid="cpq-templates-tab">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-muted2">Templates reutilizaveis que pre-preenchem uma nova proposta com itens, notas, condicoes e validade.</div>
        <Btn onClick={() => { setActiveEditorTab("campos"); setEditing(empty()); }} testid="new-tpl-btn"><Plus size={16} /> Novo template</Btn>
      </div>
      <Table testid="tpl-table" columns={[
        { key: "nome", label: "Nome", render: (r) => (
          <div className="flex items-center gap-2">
            <span className="font-semibold text-navy">{r.nome}</span>
            {r.competitive_upgrade && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-1"><Award size={9} /> CU</span>}
          </div>
        ) },
        { key: "plataforma", label: "Plataforma", render: (r) => r.plataforma || "-" },
        { key: "duracao_subscricao", label: "Duracao", render: (r) => r.duracao_subscricao ? `${r.duracao_subscricao} ano${r.duracao_subscricao > 1 ? "s" : ""}` : "1 ano" },
        { key: "itens", label: "Linhas", render: (r) => (r.itens || []).length },
        { key: "condicoes_pagamento", label: "Cond.", render: (r) => `${r.condicoes_pagamento || "-"} / ${r.metodo_pagamento || "-"}` },
        { key: "validade_dias", label: "Validade", render: (r) => `${r.validade_dias || 30}d` },
        { key: "ativo", label: "Estado", render: (r) => r.ativo ? <Badge value="ativo" /> : <Badge value="inativo" /> },
        { key: "acoes", label: "", render: (r) => (
          <div className="flex gap-1">
            <button onClick={() => { setActiveEditorTab("campos"); setEditing(r); }} data-testid={`tpl-edit-${r.id}`} className="text-navy hover:bg-tint rounded p-1 text-xs">Editar</button>
            <button onClick={() => del(r.id)} data-testid={`tpl-del-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>
          </div>
        ) },
      ]} rows={rows} />
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? `Editar: ${editing.nome}` : "Novo template"} testid="tpl-modal">
        {editing && (
          <div className="space-y-3">
            <div className="flex gap-1 border-b border-slate-200 pb-0">
              {[{ k: "campos", label: "Campos e Linhas" }, { k: "corpo", label: "Corpo WYSIWYG" }].map((t) => (
                <button key={t.k} onClick={() => setActiveEditorTab(t.k)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-t border-b-2 transition-colors ${activeEditorTab === t.k ? "border-blue text-blue bg-blue/5" : "border-transparent text-slate-500 hover:text-navy"}`}
                  data-testid={`tpl-tab-${t.k}`}>{t.label}</button>
              ))}
            </div>
            {activeEditorTab === "campos" && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Nome" value={editing.nome} onChange={(v) => setEditing({ ...editing, nome: v })} testid="tpl-nome" />
                  <Field label="Plataforma (opcional)" value={editing.plataforma} onChange={(v) => setEditing({ ...editing, plataforma: v })} testid="tpl-plataforma" />
                </div>
                <TextArea label="Descricao curta" value={editing.descricao} onChange={(v) => setEditing({ ...editing, descricao: v })} testid="tpl-desc" />
                <div className="grid grid-cols-2 gap-3 bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div>
                    <label className="text-xs font-semibold text-navy block mb-1.5">Duracao da subscricao</label>
                    <div className="flex gap-2">
                      {DURACOES.map((d) => (
                        <button key={d.value} type="button" onClick={() => setEditing({ ...editing, duracao_subscricao: d.value })}
                          data-testid={`tpl-duracao-${d.value}`}
                          className={`flex-1 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${editing.duracao_subscricao === d.value ? "border-blue bg-blue text-white" : "border-slate-200 text-slate-600 hover:border-blue/40"}`}>
                          {d.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-3 cursor-pointer select-none p-3 rounded-lg border-2 border-dashed w-full transition-colors hover:border-amber-300" style={{ borderColor: editing.competitive_upgrade ? "#f59e0b" : undefined, background: editing.competitive_upgrade ? "#fef3c740" : undefined }}>
                      <input type="checkbox" checked={!!editing.competitive_upgrade} onChange={(e) => setEditing({ ...editing, competitive_upgrade: e.target.checked })} data-testid="tpl-competitive-upgrade" className="w-4 h-4 accent-amber-500" />
                      <div>
                        <div className="text-sm font-bold text-navy flex items-center gap-1"><Award size={13} className="text-amber-500" /> Competitive Upgrade</div>
                        <div className="text-[10px] text-slate-400">Proposta de substituicao de produto concorrente</div>
                      </div>
                    </label>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1"><label className="text-sm font-semibold text-navy">Linhas default</label><button onClick={addItem} data-testid="tpl-add-item" className="text-blue text-sm hover:underline">+ Linha</button></div>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {(editing.itens || []).map((it, i) => (
                      <div key={i} className="grid grid-cols-12 gap-1.5 items-center">
                        <input className="col-span-5 px-2 py-1.5 border border-tint rounded text-xs" placeholder="Descricao" value={it.descricao} onChange={(e) => setItem(i, "descricao", e.target.value)} data-testid={`tpl-item-desc-${i}`} />
                        <select className="col-span-2 px-1 py-1.5 border border-tint rounded text-xs" value={it.periodo} onChange={(e) => setItem(i, "periodo", e.target.value)}>
                          <option value="unica">Unica</option><option value="mensal">Mensal</option><option value="anual">Anual</option>
                          <option value="3anos">3 anos</option><option value="5anos">5 anos</option>
                        </select>
                        <input className="col-span-1 px-2 py-1.5 border border-tint rounded text-xs" type="number" value={it.quantidade} onChange={(e) => setItem(i, "quantidade", e.target.value)} />
                        <input className="col-span-2 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="Preco" value={it.preco_unitario} onChange={(e) => setItem(i, "preco_unitario", e.target.value)} />
                        <input className="col-span-1 px-2 py-1.5 border border-tint rounded text-xs" type="number" placeholder="IVA%" value={it.iva_pct} onChange={(e) => setItem(i, "iva_pct", e.target.value)} />
                        <button className="col-span-1 text-red-500" onClick={() => rmItem(i)} data-testid={`tpl-item-rm-${i}`}><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <Field label="Desconto global %" type="number" value={editing.desconto_pct_global} onChange={(v) => setEditing({ ...editing, desconto_pct_global: v })} />
                  <Field label="Validade (dias)" type="number" value={editing.validade_dias} onChange={(v) => setEditing({ ...editing, validade_dias: v })} />
                  <Selecter label="Cond. pagamento" value={editing.condicoes_pagamento} onChange={(v) => setEditing({ ...editing, condicoes_pagamento: v })} options={["pronto", "30d", "60d", "90d"]} />
                  <Selecter label="Metodo pagamento" value={editing.metodo_pagamento} onChange={(v) => setEditing({ ...editing, metodo_pagamento: v })} options={["sepa", "mb", "cartao", "transferencia"]} />
                </div>
                <TextArea label="Notas (aparecem na proposta)" value={editing.notas} onChange={(v) => setEditing({ ...editing, notas: v })} testid="tpl-notas" />
                <label className="flex items-center gap-2 text-sm text-navy pt-1">
                  <input type="checkbox" checked={!!editing.ativo} onChange={(e) => setEditing({ ...editing, ativo: e.target.checked })} data-testid="tpl-ativo" />
                  Template activo (aparece no picker)
                </label>
              </>
            )}
            {activeEditorTab === "corpo" && (
              <div>
                <div className="text-xs text-slate-500 mb-2">Corpo do documento. Usa variaveis como {"{{nome_cliente}}"}.</div>
                <WysiwygEditor value={editing.corpo_html || ""} onChange={(v) => setEditing({ ...editing, corpo_html: v })} placeholder="Escreve o corpo da proposta..." minHeight={350} variables={VARS} />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancelar</Btn>
              <Btn onClick={save} testid="tpl-save">Guardar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


function CoverLettersTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/cpq/cover-letters"); setRows(data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const empty = () => ({ nome: "", subject_template: "Proposta {proposta_numero} da Cogniseg", body_html: "<p>Ola {cliente_nome},</p>\n<p>Envio a proposta <b>{proposta_numero}</b>. <a href='{link_publico}'>Ver aqui</a>.</p>", ativo: true });

  const save = async () => {
    if (!editing.nome?.trim()) return toast.error("Nome obrigatorio.");
    try {
      if (editing.id) await api.put(`/cpq/cover-letters/${editing.id}`, editing);
      else await api.post("/cpq/cover-letters", editing);
      toast.success("Guardado."); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Eliminar template?")) return;
    try { await api.delete(`/cpq/cover-letters/${id}`); toast.success("Eliminado."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="cover-letters-tab">
      <div className="flex justify-between items-center mb-3">
        <div className="text-sm text-muted2">Templates de carta de apresentacao para acompanhar o link publico da proposta por email. Placeholders: {"{cliente_nome} {proposta_numero} {proposta_titulo} {total} {moeda} {validade} {link_publico} {me_nome}"}.</div>
        <Btn onClick={() => setEditing(empty())} testid="new-cl-btn"><Plus size={16} /> Novo template</Btn>
      </div>
      <Table testid="cl-table" columns={[
        { key: "nome", label: "Nome", render: (r) => <b className="text-navy">{r.nome}</b> },
        { key: "subject_template", label: "Assunto (template)" },
        { key: "ativo", label: "Estado", render: (r) => r.ativo ? <Badge value="ativo" /> : <Badge value="inativo" /> },
        { key: "acoes", label: "", render: (r) => (
          <div className="flex gap-1">
            <button onClick={() => setEditing(r)} data-testid={`cl-edit-${r.id}`} className="text-navy hover:bg-tint rounded p-1 text-xs">Editar</button>
            <button onClick={() => del(r.id)} data-testid={`cl-del-${r.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={14} /></button>
          </div>
        ) },
      ]} rows={rows} />
      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.id ? `Editar: ${editing.nome}` : "Novo template"} testid="cl-modal">
        {editing && (
          <div className="space-y-2">
            <Field label="Nome" value={editing.nome} onChange={(v) => setEditing({ ...editing, nome: v })} testid="cl-nome" />
            <Field label="Assunto (template)" value={editing.subject_template} onChange={(v) => setEditing({ ...editing, subject_template: v })} testid="cl-subject" />
            <TextArea label="Body HTML" value={editing.body_html} onChange={(v) => setEditing({ ...editing, body_html: v })} testid="cl-body" rows={12} />
            <label className="flex items-center gap-2 text-sm text-navy">
              <input type="checkbox" checked={!!editing.ativo} onChange={(e) => setEditing({ ...editing, ativo: e.target.checked })} data-testid="cl-ativo" />
              Ativo
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setEditing(null)}>Cancelar</Btn>
              <Btn onClick={save} testid="cl-save">Guardar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function TaskDeadlinePrefsTab() {
  const [prefs, setPrefs] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/team/notification-preferences"); setPrefs(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const save = async () => {
    try {
      const { data } = await api.put("/team/notification-preferences", {
        deadline_notify_hours: parseInt(prefs.deadline_notify_hours) || 24,
        deadline_notify_email: !!prefs.deadline_notify_email,
        deadline_notify_telegram: !!prefs.deadline_notify_telegram,
        deadline_notify_min_priority: prefs.deadline_notify_min_priority || "baixa",
      });
      setPrefs(data); toast.success("Preferencias guardadas.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!prefs) return <div className="text-sm text-muted2">A carregar...</div>;
  return (
    <div className="max-w-2xl" data-testid="task-prefs-tab">
      <SectionCard title="Alertas quando um prazo se aproxima" hint="Configure quando quer receber notificacoes das suas tarefas com prazo. O job corre a cada 30 minutos e so envia uma vez por tarefa.">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Alertar quando faltarem X horas" type="number" value={prefs.deadline_notify_hours}
            onChange={(v) => setPrefs({ ...prefs, deadline_notify_hours: v })} testid="prefs-hours" />
          <Selecter label="Prioridade minima" value={prefs.deadline_notify_min_priority}
            onChange={(v) => setPrefs({ ...prefs, deadline_notify_min_priority: v })}
            options={["baixa", "media", "alta", "critica"]} testid="prefs-min-prio" />
        </div>
        <div className="mt-3 space-y-1.5">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!prefs.deadline_notify_email}
              onChange={(e) => setPrefs({ ...prefs, deadline_notify_email: e.target.checked })}
              data-testid="prefs-email" />
            <span>Enviar por email</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!prefs.deadline_notify_telegram}
              onChange={(e) => setPrefs({ ...prefs, deadline_notify_telegram: e.target.checked })}
              data-testid="prefs-telegram" />
            <span>Enviar por Telegram (precisa de telegram_chat_id no perfil e bot configurado)</span>
          </label>
        </div>
        <div className="flex justify-end pt-3">
          <Btn onClick={save} testid="prefs-save">Guardar</Btn>
        </div>
      </SectionCard>
    </div>
  );
}


function FunisTab() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [nome, setNome] = useState("");
  const [plataforma, setPlataforma] = useState("");

  const load = async () => { try { const { data } = await api.get("/pipelines"); setRows(data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async () => {
    if (!nome.trim()) return toast.error("Indique o nome.");
    try { await api.post("/pipelines", { nome, plataforma, stages: [] }); toast.success("Funil criado."); setShow(false); setNome(""); setPlataforma(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/pipelines/${id}`); toast.success("Funil removido."); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div data-testid="funis-tab">
      <div className="flex justify-end mb-3"><Btn onClick={() => setShow(true)} testid="new-pipeline-btn"><Plus size={16} /> Novo funil</Btn></div>
      <div className="space-y-3">
        {rows.map((p) => (
          <div key={p.id} className="border border-tint rounded-lg p-4" data-testid={`pipeline-${p.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div><span className="font-semibold text-navy">{p.nome}</span> {p.plataforma && <span className="text-xs text-muted2">- {p.plataforma}</span>} {p.predefinido && <Badge value="ativo" />}</div>
              {!p.predefinido && <button onClick={() => remove(p.id)} data-testid={`del-pipeline-${p.id}`} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 size={15} /></button>}
            </div>
            <div className="flex flex-wrap gap-2">
              {(p.stages || []).map((s) => (
                <span key={s.key} className="px-2.5 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: s.cor }}>{s.nome} ({s.probabilidade}%)</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Modal open={show} onClose={() => setShow(false)} title="Novo funil" testid="pipeline-modal">
        <p className="text-xs text-muted2">O funil e criado com as fases padrao (Novo, Qualificado, Proposta, Negociacao, Ganho, Perdido). A edicao de fases individuais chega numa iteracao seguinte.</p>
        <Field label="Nome" value={nome} onChange={setNome} placeholder="Funil CogniEtica" testid="pipeline-nome" />
        <label className="block text-sm font-semibold text-navy mb-1">Plataforma (opcional)</label>
        <PlatformPicker value={plataforma} onChange={setPlataforma} testid="pipeline-plataforma" placeholder="Todas as plataformas" />
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={() => setShow(false)}>Cancelar</Btn><Btn onClick={create} testid="pipeline-save">Criar</Btn></div>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Qualificacao de Leads - configuracao de pesos e regras
// ---------------------------------------------------------------------------
function QualificacaoTab() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/crm/qualify/config");
      setCfg(data?.config || null);
      setDefaults(data?.defaults || null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setWeight = (k, v) => {
    const n = Math.max(0, Number(v) || 0);
    setCfg({ ...cfg, weights: { ...cfg.weights, [k]: n } });
  };
  const setMapValue = (mapKey, k, v) => {
    const n = Math.max(0, Number(v) || 0);
    setCfg({ ...cfg, [mapKey]: { ...cfg[mapKey], [k]: n } });
  };
  const setThreshold = (k, v) => {
    const n = Math.max(0, Math.min(100, Number(v) || 0));
    setCfg({ ...cfg, thresholds: { ...cfg.thresholds, [k]: n } });
  };
  const setRecenciaBucket = (idx, field, v) => {
    const arr = [...(cfg.recencia_buckets || [])];
    arr[idx] = { ...arr[idx], [field]: Math.max(0, Number(v) || 0) };
    setCfg({ ...cfg, recencia_buckets: arr });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/crm/qualify/config", { config: cfg });
      toast.success("Configuracao guardada.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Repor as boas praticas padrao? A configuracao actual sera substituida.")) return;
    setSaving(true);
    try {
      await api.post("/crm/qualify/config/reset");
      toast.success("Defaults repostos.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const recompute = async () => {
    setRecomputing(true);
    try {
      const { data } = await api.post("/crm/qualify/recompute-all");
      toast.success(`Recalculo concluido: ${data.n}/${data.total} contas.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRecomputing(false); }
  };

  if (!cfg) return <div className="text-sm text-muted2">A carregar...</div>;

  const wSum = Object.values(cfg.weights || {}).reduce((a, b) => a + (Number(b) || 0), 0);

  return (
    <div className="space-y-5" data-testid="qualificacao-tab">
      {/* Header com formula + KPIs de config + acoes */}
      <div className="bg-gradient-to-br from-blue/5 via-white to-slate-50 border border-blue/20 rounded-xl p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-7 h-7 rounded-lg bg-blue/10 text-blue flex items-center justify-center text-[11px] font-bold">LS</div>
              <h3 className="text-navy font-bold text-base">Configuracao de qualificacao</h3>
            </div>
            <p className="text-[12px] text-slate-700 leading-relaxed">
              <b>Formula:</b> lead_score = fit + completude + engagement + origem + recencia (0-100).
              O engagement agrega chat, handoffs, emails, tickets, formularios, reunioes e notas.
              Rating (hot/warm/cold) e ciclo de vida sugerido dependem dos thresholds.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${wSum === 100 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : "text-amber-700 bg-amber-50 border-amber-200"}`} data-testid="weight-sum-badge">
              Soma dos pesos: {wSum}{wSum !== 100 ? " - recomendado 100" : " OK"}
            </span>
            <div className="flex gap-1.5">
              <Btn onClick={save} disabled={saving} testid="qualificacao-save">
                {saving ? "A guardar..." : "Guardar"}
              </Btn>
              <Btn variant="ghost" onClick={recompute} disabled={recomputing} testid="qualificacao-recompute">
                {recomputing ? "A recalcular..." : "Recalcular"}
              </Btn>
              <Btn variant="ghost" onClick={resetDefaults} disabled={saving} testid="qualificacao-reset">
                Repor
              </Btn>
            </div>
          </div>
        </div>
      </div>

      {/* Row 1: Pesos por componente (5 sliders/inputs) - full width */}
      <SectionCard title="Pesos por componente" hint="Teto de cada componente. Total recomendado: 100.">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            {k: "fit",         label: "Fit ICP",      color: "bg-violet-500"},
            {k: "completude",  label: "Completude",   color: "bg-sky-500"},
            {k: "engagement",  label: "Engagement",   color: "bg-emerald-500"},
            {k: "origem",      label: "Origem",       color: "bg-amber-500"},
            {k: "recencia",    label: "Recencia",     color: "bg-rose-500"},
          ].map((c) => {
            const v = cfg.weights?.[c.k] ?? 0;
            const pct = Math.min(100, (Number(v) / 100) * 100);
            return (
              <div key={c.k} className="border border-tint rounded-lg p-3 bg-white hover:border-blue transition-colors">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{c.label}</div>
                <div className="flex items-center gap-2 mb-2">
                  <input type="number" min={0} max={100} value={v}
                    onChange={(e) => setWeight(c.k, e.target.value)}
                    data-testid={`weight-${c.k}`}
                    className="w-16 px-2 py-1 text-lg font-bold text-navy border border-tint rounded text-center" />
                  <span className="text-xs text-muted2">/ 100</span>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${c.color}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Row 2: Thresholds (4 inputs) - full width com cores por rating */}
      <SectionCard title="Thresholds de rating e ciclo de vida" hint="Score minimo para cada classificacao.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            {k: "hot",  label: "HOT",  desc: "Prioridade maxima",       tone: "red"},
            {k: "warm", label: "WARM", desc: "Em nutricao",             tone: "amber"},
            {k: "sql",  label: "SQL",  desc: "Pronta para vendas",      tone: "emerald"},
            {k: "mql",  label: "MQL",  desc: "Qualificada por marketing", tone: "sky"},
          ].map((f) => {
            const tones = {
              red:     "border-red-200 bg-red-50 text-red-700",
              amber:   "border-amber-200 bg-amber-50 text-amber-700",
              emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
              sky:     "border-sky-200 bg-sky-50 text-sky-700",
            };
            return (
              <div key={f.k} className={`border rounded-lg p-3 ${tones[f.tone]}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-wider font-bold">{f.label}</span>
                  <span className="text-[10px] opacity-75">score {'>='}</span>
                </div>
                <input type="number" min={0} max={100} value={cfg.thresholds?.[f.k] ?? 0}
                  onChange={(e) => setThreshold(f.k, e.target.value)}
                  data-testid={`threshold-${f.k}`}
                  className="w-full px-2 py-1.5 text-lg font-bold text-navy border border-white/60 bg-white/70 rounded" />
                <div className="text-[10px] mt-1 opacity-80">{f.desc}</div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* Row 3: Fit por dimensao + Recencia buckets (2 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Fit por dimensao (ICP)" hint="Pontos por dimensao da empresa.">
          <div className="space-y-2">
            {Object.entries(cfg.dimensao_score || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-slate-50">
                <span className="text-xs font-semibold text-navy w-28 uppercase">{k}</span>
                <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-violet-400" style={{ width: `${Math.min(100, (v / 30) * 100)}%` }} />
                </div>
                <input type="number" min={0} value={v}
                  onChange={(e) => setMapValue("dimensao_score", k, e.target.value)}
                  data-testid={`dim-${k}`}
                  className="w-16 px-2 py-1 text-sm font-mono text-navy border border-tint rounded text-center" />
              </div>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Recencia (dias -> pontos)" hint="Quanto mais recente a interacao, mais pontos.">
          <div className="space-y-2">
            {(cfg.recencia_buckets || []).map((b, i) => (
              <div key={i} className="grid grid-cols-[auto_1fr_auto] items-center gap-3 py-1.5 px-2 rounded hover:bg-slate-50">
                <span className="text-xs font-semibold text-navy uppercase whitespace-nowrap">Ate</span>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} value={b.max_dias}
                    onChange={(e) => setRecenciaBucket(i, "max_dias", e.target.value)}
                    data-testid={`recencia-max-${i}`}
                    className="w-16 px-2 py-1 text-sm font-mono text-navy border border-tint rounded text-center" />
                  <span className="text-xs text-muted2">dias</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase text-slate-500 font-semibold">Pts</span>
                  <input type="number" min={0} value={b.pts}
                    onChange={(e) => setRecenciaBucket(i, "pts", e.target.value)}
                    data-testid={`recencia-pts-${i}`}
                    className="w-16 px-2 py-1 text-sm font-mono text-navy border border-tint rounded text-center" />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      </div>

      {/* Row 4: Peso por origem - full width, 2 columns internally */}
      <SectionCard title="Peso por origem" hint="Canal de captura da lead. Cada slug e uma origem distinta (chat, formularios e handoffs sao separados).">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
          {Object.entries(cfg.origem_score || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-slate-50" data-testid={`origem-row-${k}`}>
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[11px] text-slate-500 truncate">{k}</div>
                <div className="text-[11px] text-navy truncate">{ORIGEM_LABELS[k] || <span className="italic text-slate-400">(sem descricao)</span>}</div>
              </div>
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[100px]">
                <div className="h-full bg-amber-400" style={{ width: `${Math.min(100, (v / 10) * 100)}%` }} />
              </div>
              <input type="number" min={0} value={v}
                onChange={(e) => setMapValue("origem_score", k, e.target.value)}
                data-testid={`origem-${k}`}
                className="w-16 px-2 py-1 text-sm font-mono text-navy border border-tint rounded text-center" />
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Row 5: Peso por tipo de interacao (engagement) - full width, 3 col */}
      <SectionCard title="Peso por tipo de interacao (engagement)"
        hint="Multiplica pelo tipo de cada interacao. Cobre atividades CRM, chat AI, handoffs, emails inbound/outbound, tickets, formularios, reunioes agendadas.">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-1.5">
          {Object.entries(cfg.atividade_peso || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50">
              <span className="text-xs text-slate-700 flex-1 truncate font-mono" title={k}>{k}</span>
              <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-400" style={{ width: `${Math.min(100, (v / 10) * 100)}%` }} />
              </div>
              <input type="number" min={0} value={v}
                onChange={(e) => setMapValue("atividade_peso", k, e.target.value)}
                data-testid={`atv-${k}`}
                className="w-14 px-2 py-1 text-sm font-mono text-navy border border-tint rounded text-center" />
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Rodape de acoes - repete botoes para acesso rapido */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-tint py-3 flex flex-wrap items-center gap-2 -mx-2 px-2">
        <Btn onClick={save} disabled={saving} testid="qualificacao-save-bottom">
          {saving ? "A guardar..." : "Guardar configuracao"}
        </Btn>
        <Btn variant="ghost" onClick={recompute} disabled={recomputing} testid="qualificacao-recompute-bottom">
          {recomputing ? "A recalcular..." : "Recalcular todas as contas"}
        </Btn>
        <Btn variant="ghost" onClick={resetDefaults} disabled={saving} testid="qualificacao-reset-bottom">
          Repor boas praticas
        </Btn>
        <span className="ml-auto text-[11px] text-muted2">Guarde as alteracoes antes de recalcular.</span>
      </div>
    </div>
  );
}

function SectionCardOldRemoved() { return null; } // eslint-disable-line no-unused-vars

// ---------------------------------------------------------------------------
// Leads (Qualificacao + Automatismos)
// ---------------------------------------------------------------------------
const ORIGEM_LABELS = {
  "referral": "Referral (recomendacao)",
  "demo": "Demo agendada",
  "agendamento": "Agendamento direto",
  "chamada": "Chamada inbound",
  "email": "Email inbound",
  "formulario": "Formulario interno",
  "formulario-website": "Formulario publico do site",
  "chat": "Chat generico",
  "chat-ai": "Chat AI (bot)",
  "chat-ai-humano-comercial": "Chat AI escalado - comercial",
  "chat-ai-humano-tecnico": "Chat AI escalado - tecnico",
  "chat-ai-handoff": "Handoff do chat para humano",
  "webinar": "Webinar",
  "evento": "Evento/feira",
  "landing": "Landing page",
  "manual": "Introducao manual",
};

function LeadsTab() {
  const [sub, setSub] = useState("qualificacao");
  return (
    <div data-testid="leads-tab">
      <SubTabs
        testid="leads-subtabs"
        current={sub}
        onChange={setSub}
        tabs={[
          { key: "qualificacao", label: "Qualificacao" },
          { key: "automatismos", label: "Automatismos por timing" },
        ]}
      />
      {sub === "qualificacao" && <QualificacaoTab />}
      {sub === "automatismos" && <AutomatismosTab />}
    </div>
  );
}

const TIMING_ACOES = [
  { value: "email",    label: "Email" },
  { value: "chamada",  label: "Chamada" },
  { value: "reuniao",  label: "Reuniao" },
  { value: "demo",     label: "Demo" },
  { value: "tarefa",   label: "Tarefa generica" },
  { value: "follow-up",label: "Follow-up" },
];
const TIMING_PRIORIDADES = [
  { value: "alta",  label: "Alta" },
  { value: "media", label: "Media" },
  { value: "baixa", label: "Baixa" },
];

function AutomatismosTab() {
  const [cfg, setCfg] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/crm/qualify/automations");
      setCfg(data?.config || null);
      setBuckets(data?.buckets || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setRule = (idx, field, v) => {
    const rules = [...(cfg.rules || [])];
    rules[idx] = { ...rules[idx], [field]: v };
    setCfg({ ...cfg, rules });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/crm/qualify/automations", { config: cfg });
      toast.success("Automatismos guardados.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Repor as regras padrao? A configuracao actual sera substituida.")) return;
    setSaving(true);
    try {
      await api.post("/crm/qualify/automations/reset");
      toast.success("Defaults repostos.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-sm text-muted2">A carregar...</div>;

  return (
    <div className="max-w-5xl space-y-5" data-testid="automatismos-tab">
      <div className="border border-blue/20 bg-blue/5 rounded-lg p-3 text-[12px] text-slate-700">
        <b className="text-navy">Como funciona:</b> quando a BANT-timing de uma lead entra na janela de aviso definida, o Hub cria automaticamente uma tarefa (email/chamada/reuniao) para o responsavel da conta. As tarefas aparecem em <code>/app/tarefas</code>. O job corre a cada 12h e nao duplica.
      </div>

      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
          data-testid="autom-enabled" className="w-4 h-4" />
        Automatismos ativos
      </label>

      <div className="border border-tint rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Timing</th>
              <th className="text-left px-3 py-2">Ativo</th>
              <th className="text-left px-3 py-2">Aviso (dias antes fim)</th>
              <th className="text-left px-3 py-2">Accao</th>
              <th className="text-left px-3 py-2">Prioridade</th>
              <th className="text-left px-3 py-2">Titulo tarefa</th>
            </tr>
          </thead>
          <tbody>
            {(cfg.rules || []).map((r, i) => (
              <tr key={i} className="border-t border-tint" data-testid={`autom-rule-${r.timing}`}>
                <td className="px-3 py-2 font-semibold text-navy">{r.timing}</td>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={!!r.enabled}
                    onChange={(e) => setRule(i, "enabled", e.target.checked)}
                    data-testid={`autom-enabled-${r.timing}`} />
                </td>
                <td className="px-3 py-2">
                  <input type="number" min={0} value={r.dias_antes_fim ?? 0}
                    onChange={(e) => setRule(i, "dias_antes_fim", Number(e.target.value) || 0)}
                    data-testid={`autom-dias-${r.timing}`}
                    className="w-20 px-2 py-1 border border-tint rounded text-sm" />
                </td>
                <td className="px-3 py-2">
                  <select value={r.acao || "email"} onChange={(e) => setRule(i, "acao", e.target.value)}
                    data-testid={`autom-acao-${r.timing}`}
                    className="px-2 py-1 border border-tint rounded text-sm bg-white">
                    {TIMING_ACOES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <select value={r.prioridade || "media"} onChange={(e) => setRule(i, "prioridade", e.target.value)}
                    data-testid={`autom-prio-${r.timing}`}
                    className="px-2 py-1 border border-tint rounded text-sm bg-white">
                    {TIMING_PRIORIDADES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input value={r.titulo || ""} onChange={(e) => setRule(i, "titulo", e.target.value)}
                    data-testid={`autom-titulo-${r.timing}`}
                    className="w-full px-2 py-1 border border-tint rounded text-sm" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-tint rounded-lg p-3 flex items-center gap-3">
        <label className="text-xs text-slate-600">Cadencia para timing "indefinido" (dias):</label>
        <input type="number" min={1} value={cfg.indefinido_cadencia_dias ?? 30}
          onChange={(e) => setCfg({ ...cfg, indefinido_cadencia_dias: Number(e.target.value) || 30 })}
          data-testid="autom-cadencia-indef"
          className="w-24 px-2 py-1 border border-tint rounded text-sm" />
      </div>

      {buckets.length > 0 && (
        <div className="border border-tint rounded-lg p-3">
          <div className="text-[11px] font-semibold text-slate-700 mb-2">Distribuicao actual (informativo)</div>
          <div className="flex flex-wrap gap-1.5">
            {buckets.map((b) => (
              <span key={b} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 border border-tint text-slate-700">
                {b}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-tint">
        <Btn onClick={save} disabled={saving} testid="autom-save">
          {saving ? "A guardar..." : "Guardar automatismos"}
        </Btn>
        <Btn variant="ghost" onClick={reset} disabled={saving} testid="autom-reset">
          Repor boas praticas
        </Btn>
        <a href="/app/tarefas" className="text-[11px] font-semibold text-blue hover:underline self-center">Ver tarefas geradas</a>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Shortlinks (Iter97): metricas de acessos aos documentos partilhados por SMS
// ---------------------------------------------------------------------------
function ShortlinksTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [filtroEstado, setFiltroEstado] = useState(""); // "", "ativo", "expirado"
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/shortlinks", { params: { limit: 500 } }); setRows(data || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (sid) => {
    if (!window.confirm("Eliminar este shortlink? Deixa de funcionar imediatamente.")) return;
    try { await api.delete(`/shortlinks/${sid}`); toast.success("Shortlink eliminado."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("URL copiado."); }
    catch { toast.error("Nao foi possivel copiar."); }
  };

  const backend = (typeof window !== "undefined" && window.__ENV__?.REACT_APP_BACKEND_URL) || process.env.REACT_APP_BACKEND_URL || "";

  const filtered = rows.filter((r) => {
    if (filtroTipo && r.doc_tipo !== filtroTipo) return false;
    if (filtroEstado === "ativo" && r.expirado) return false;
    if (filtroEstado === "expirado" && !r.expirado) return false;
    if (q) {
      const needle = q.toLowerCase();
      if (!(r.titulo || "").toLowerCase().includes(needle)
       && !(r.account_nome || "").toLowerCase().includes(needle)
       && !(r.code || "").toLowerCase().includes(needle)) return false;
    }
    return true;
  });

  const totalHits = filtered.reduce((s, r) => s + (r.hits || 0), 0);
  const ativos = filtered.filter((r) => !r.expirado).length;

  return (
    <div data-testid="shortlinks-tab">
      <SettingsHero
        eyebrow="Canais / SMS"
        title="Shortlinks de documentos"
        description="Links publicos que geraste ao partilhar propostas, contratos, faturas ou recibos via SMS. Ve quem clicou e afere a eficacia das mensagens enviadas."
      />
      <SectionCard title="Metricas e gestao" hint="Consulta acessos, revoga links ativos e valida a validade configurada.">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="rounded-md border border-tint p-3 bg-slate-50/60">
            <div className="text-[10px] font-semibold text-muted2 uppercase">Total links</div>
            <div className="text-xl font-bold text-navy" data-testid="shortlinks-kpi-total">{filtered.length}</div>
          </div>
          <div className="rounded-md border border-tint p-3 bg-slate-50/60">
            <div className="text-[10px] font-semibold text-muted2 uppercase">Ativos</div>
            <div className="text-xl font-bold text-emerald-600" data-testid="shortlinks-kpi-ativos">{ativos}</div>
          </div>
          <div className="rounded-md border border-tint p-3 bg-slate-50/60">
            <div className="text-[10px] font-semibold text-muted2 uppercase">Expirados</div>
            <div className="text-xl font-bold text-amber-600" data-testid="shortlinks-kpi-expirados">{filtered.length - ativos}</div>
          </div>
          <div className="rounded-md border border-tint p-3 bg-slate-50/60">
            <div className="text-[10px] font-semibold text-muted2 uppercase">Total acessos</div>
            <div className="text-xl font-bold text-blue" data-testid="shortlinks-kpi-hits">{totalHits}</div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Procurar por conta, titulo ou codigo..."
            data-testid="shortlinks-search"
            className="px-3 py-1.5 text-sm border border-tint rounded w-72" />
          <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
            data-testid="shortlinks-filter-tipo"
            className="px-2 py-1.5 text-sm border border-tint rounded bg-white">
            <option value="">Todos os tipos</option>
            <option value="fatura">Fatura</option>
            <option value="recibo">Recibo</option>
            <option value="proposta">Proposta</option>
            <option value="contrato">Contrato</option>
          </select>
          <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
            data-testid="shortlinks-filter-estado"
            className="px-2 py-1.5 text-sm border border-tint rounded bg-white">
            <option value="">Todos os estados</option>
            <option value="ativo">Ativos</option>
            <option value="expirado">Expirados</option>
          </select>
          <Btn variant="ghost" onClick={load} testid="shortlinks-reload">Recarregar</Btn>
        </div>

        {loading ? (
          <div className="text-sm text-muted2 italic">A carregar...</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-muted2 italic" data-testid="shortlinks-empty">Sem shortlinks para os filtros escolhidos.</div>
        ) : (
          <Table
            testid="shortlinks-table"
            columns={[
              { key: "codigo", label: "Codigo" },
              { key: "tipo", label: "Tipo" },
              { key: "titulo", label: "Titulo" },
              { key: "conta", label: "Conta" },
              { key: "criado", label: "Criado" },
              { key: "expira", label: "Expira" },
              { key: "hits", label: "Acessos" },
              { key: "acc", label: "" },
            ]}
            rows={filtered.map((r) => {
              const url = backend ? `${backend}/api/s/${r.code}` : `/api/s/${r.code}`;
              return {
                codigo: <span className="font-mono text-[12px] text-navy">{r.code}</span>,
                tipo: (() => {
                  const tipoColor = r.doc_tipo === "fatura" ? "bg-amber-100 text-amber-800 border-amber-200"
                    : r.doc_tipo === "recibo" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                    : r.doc_tipo === "proposta" ? "bg-blue-100 text-blue-800 border-blue-200"
                    : "bg-navy/10 text-navy border-navy/20";
                  return <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[11px] font-semibold uppercase tracking-wide ${tipoColor}`}>{r.doc_tipo}</span>;
                })(),
                titulo: <span className="text-navy text-[12.5px]" title={r.titulo}>{r.titulo || "-"}</span>,
                conta: <span className="text-navy text-[12.5px]">{r.account_nome || <span className="text-muted2 italic">(conta removida)</span>}</span>,
                criado: <span className="text-[11.5px] text-muted2">{dt(r.criado_em)}</span>,
                expira: r.expira_em
                  ? <span className={`text-[11.5px] ${r.expirado ? "text-red-600 font-semibold" : "text-navy"}`}>{dt(r.expira_em)}{r.expirado ? " (expirado)" : ""}</span>
                  : <span className="text-[11.5px] text-muted2">Permanente</span>,
                hits: <span className={`font-semibold ${(r.hits || 0) > 0 ? "text-blue" : "text-muted2"}`} title={r.ultimo_hit_em ? `Ultimo: ${dt(r.ultimo_hit_em)}` : ""}>{r.hits || 0}</span>,
                acc: (
                  <div className="flex items-center gap-1 justify-end">
                    <Btn variant="ghost" size="sm" onClick={() => copy(url)} testid={`shortlinks-copy-${r.code}`}>Copiar URL</Btn>
                    <button onClick={() => del(r.id)} data-testid={`shortlinks-del-${r.code}`}
                      className="text-red-500 hover:text-red-700 p-1"><Trash2 size={13} /></button>
                  </div>
                ),
              };
            })}
          />
        )}
      </SectionCard>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documentacao (Iter100) - download dos MD do /app/memory/
// ---------------------------------------------------------------------------
function DocumentacaoTab() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/memory-docs"); setFiles(data.files || []); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadWithAuth = async (url, filename) => {
    try {
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] || "application/octet-stream" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha no download."); }
  };

  return (
    <div data-testid="docs-tab">
      <SettingsHero
        eyebrow="Documentacao"
        title="Ficheiros MD do projecto"
        description="Documentacao interna, PRD, roadmap, integracoes e guias tecnicos. Descarregar individualmente ou em zip."
      />
      <SectionCard title="Downloads" hint={`${files.length} ficheiros disponiveis`}>
        <div className="flex gap-2 mb-3">
          <Btn onClick={() => downloadWithAuth("/memory-docs.zip", `cogniseg-hub-docs-${new Date().toISOString().slice(0,10)}.zip`)} testid="docs-zip-btn">
            Download tudo (ZIP)
          </Btn>
          <Btn variant="ghost" onClick={load}>Recarregar</Btn>
        </div>
        {loading ? (
          <div className="text-sm text-muted2 italic">A carregar...</div>
        ) : files.length === 0 ? (
          <div className="text-sm text-muted2 italic">Sem documentos.</div>
        ) : (
          <table className="w-full text-[13px]" data-testid="docs-table">
            <thead className="text-[10px] uppercase text-slate-500 border-b border-tint">
              <tr>
                <th className="text-left py-1.5">Ficheiro</th>
                <th className="text-right py-1.5 w-24">Tamanho</th>
                <th className="text-right py-1.5 w-40">Modificado</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.name} className="border-b border-tint last:border-b-0 hover:bg-slate-50/60">
                  <td className="py-1.5 text-navy font-mono text-[12px]">{f.name}</td>
                  <td className="text-right text-muted2">{(f.size / 1024).toFixed(1)} KB</td>
                  <td className="text-right text-muted2">{new Date(f.modified * 1000).toLocaleString("pt-PT")}</td>
                  <td className="text-right">
                    <button onClick={() => downloadWithAuth(`/memory-docs/${f.name}`, f.name)}
                      data-testid={`docs-dl-${f.name}`}
                      className="text-[11px] text-blue hover:underline">Download</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SectionCard>
    </div>
  );
}




function ResumoOperacionalTab() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/team/ops-summary/history");
      setHistory(r.data.history || []);
    } catch { toast.error("Erro ao carregar historico."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const triggerNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/team/ops-summary/run-now");
      toast.success(`Resumo enviado para ${r.data.destinatarios_email} destinatario(s).`);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  // Helper: calcula total de alertas criticos de um snapshot
  const totalAlertas = (h) =>
    (h.alertas?.aprovacoes || 0) + (h.alertas?.faturas_vencidas || 0) +
    (h.alertas?.tickets_abertos || 0) + (h.tarefas?.atrasadas || 0);

  // Sparkline SVG inline - mostra evolucao do total de alertas criticos (mais antigo -> mais recente)
  const Sparkline = ({ data }) => {
    if (!data || data.length < 2) return null;
    const W = 320, H = 52, PAD = 6;
    const series = [...data].reverse(); // mais antigo primeiro para o grafico
    const vals = series.map(totalAlertas);
    const maxV = Math.max(...vals, 1);
    const minV = Math.min(...vals, 0);
    const range = maxV - minV || 1;
    const pts = vals.map((v, i) => {
      const x = PAD + (i / (vals.length - 1)) * (W - PAD * 2);
      const y = PAD + ((maxV - v) / range) * (H - PAD * 2);
      return [x, y];
    });
    const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
    const areaD = `${pathD} L${pts[pts.length - 1][0].toFixed(1)},${(H - PAD).toFixed(1)} L${pts[0][0].toFixed(1)},${(H - PAD).toFixed(1)} Z`;
    const last = vals[vals.length - 1];
    const prev = vals[vals.length - 2];
    const trend = last > prev ? "up" : last < prev ? "down" : "flat";
    const lineColor = trend === "up" ? "#dc2626" : trend === "down" ? "#16a34a" : "#6b7280";
    const fillColor = trend === "up" ? "rgba(220,38,38,0.08)" : trend === "down" ? "rgba(22,163,74,0.08)" : "rgba(107,114,128,0.06)";
    return (
      <div className="bg-white border border-tint rounded-lg p-3 mb-4" data-testid="ops-sparkline">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Evolucao - Alertas criticos</span>
          <div className="flex items-center gap-2">
            <span className={`text-[11px] font-bold flex items-center gap-0.5 ${trend === "up" ? "text-red-600" : trend === "down" ? "text-emerald-600" : "text-slate-500"}`}>
              {trend === "up" ? <TrendingUp size={12} /> : trend === "down" ? <TrendingDown size={12} /> : <Minus size={12} />}
              {last} alertas (atual)
            </span>
          </div>
        </div>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 52 }}>
          {/* Area fill */}
          <path d={areaD} fill={fillColor} />
          {/* Line */}
          <path d={pathD} fill="none" stroke={lineColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          {/* Points */}
          {pts.map(([x, y], i) => (
            <g key={i}>
              <title>{series[i].slot_key?.replace("T", " ")} - {vals[i]} alertas</title>
              <circle cx={x} cy={y} r="3" fill="white" stroke={lineColor} strokeWidth="1.5" />
              {i === pts.length - 1 && (
                <circle cx={x} cy={y} r="4.5" fill={lineColor} opacity="0.9" />
              )}
            </g>
          ))}
          {/* Baseline */}
          <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#e2e8f0" strokeWidth="1" />
          {/* Labels min/max */}
          <text x={PAD} y={PAD + 1} fontSize="8" fill="#94a3b8" dominantBaseline="hanging">{maxV}</text>
          {minV > 0 && <text x={PAD} y={H - PAD - 1} fontSize="8" fill="#94a3b8" dominantBaseline="auto">{minV}</text>}
        </svg>
        <div className="flex justify-between text-[9px] text-slate-400 mt-1 px-1">
          <span>{series[0]?.slot_key?.replace("T", " ").slice(-5)}</span>
          {series.length > 2 && <span className="text-center">{series.length} snapshots</span>}
          <span>{series[series.length - 1]?.slot_key?.replace("T", " ").slice(-5)}</span>
        </div>
      </div>
    );
  };

  const Delta = ({ cur, prev }) => {
    if (prev == null || cur == null) return <span className="text-navy font-bold">{cur ?? "-"}</span>;
    const diff = cur - prev;
    if (diff === 0) return <span className="inline-flex items-center gap-0.5 text-slate-500 font-bold"><Minus size={10} />{cur}</span>;
    if (diff > 0) return <span className="inline-flex items-center gap-0.5 text-red-600 font-bold" title={`+${diff} vs anterior`}><TrendingUp size={11} />{cur}</span>;
    return <span className="inline-flex items-center gap-0.5 text-emerald-600 font-bold" title={`${diff} vs anterior`}><TrendingDown size={11} />{cur}</span>;
  };

  const MetricRow = ({ label, cur, prev }) => (
    <tr className="border-b border-tint last:border-b-0">
      <td className="py-1 pr-4 text-[12px] text-slate-600">{label}</td>
      <td className="py-1 text-right text-[12px]"><Delta cur={cur} prev={prev} /></td>
    </tr>
  );

  return (
    <div data-testid="tab-resumo-operacional" className="space-y-4">
      <SectionCard title="Resumo Operacional" description="Historico dos ultimos 10 resumos enviados. Compara a evolucao dos indicadores ao longo do dia.">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-slate-500">{history.length} envio(s) guardados</span>
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load} testid="ops-history-refresh">
              <RefreshCw size={13} /> Atualizar
            </Btn>
            <Btn onClick={triggerNow} disabled={sending} testid="ops-run-now-btn">
              <Send size={13} /> {sending ? "A enviar..." : "Enviar agora"}
            </Btn>
          </div>
        </div>
        {loading ? (
          <div className="text-center py-8 text-slate-400 text-sm">A carregar...</div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-slate-400 text-sm">
            <History size={32} className="mx-auto mb-2 opacity-30" />
            Sem historico. Use "Enviar agora" para gerar o primeiro resumo.
          </div>
        ) : (
          <>
            <Sparkline data={history} />
            <div className="space-y-2">
            {history.map((h, idx) => {
              const prev = history[idx + 1];
              const totalH = (h.alertas?.aprovacoes || 0) + (h.alertas?.faturas_vencidas || 0) + (h.alertas?.tickets_abertos || 0) + (h.tarefas?.atrasadas || 0);
              const totalP = prev ? ((prev.alertas?.aprovacoes || 0) + (prev.alertas?.faturas_vencidas || 0) + (prev.alertas?.tickets_abertos || 0) + (prev.tarefas?.atrasadas || 0)) : null;
              const isOpen = expanded === h.id;
              const diff = totalP != null ? totalH - totalP : null;
              return (
                <div key={h.id} className={`border border-tint rounded-lg overflow-hidden transition-shadow ${isOpen ? "shadow-md" : "shadow-sm hover:shadow"}`} data-testid={`ops-history-item-${h.id}`}>
                  <button onClick={() => setExpanded(isOpen ? null : h.id)}
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-white hover:bg-slate-50/80 text-left">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-mono bg-navy/10 text-navy px-2 py-0.5 rounded">{h.slot_key?.replace("T", " ")}</span>
                      <span className="text-[12px] text-navy font-semibold">
                        Alertas criticos: {totalH}
                      </span>
                      {diff != null && (
                        <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded ${diff > 0 ? "bg-red-50 text-red-600" : diff < 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                          {diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : "="}
                        </span>
                      )}
                      {idx === 0 && <span className="text-[10px] bg-blue/10 text-blue px-1.5 py-0.5 rounded font-semibold">Mais recente</span>}
                    </div>
                    <span className="text-[10px] text-slate-400">{isOpen ? "Fechar" : "Expandir"}</span>
                  </button>
                  {isOpen && (
                    <div className="border-t border-tint bg-slate-50/60 px-4 py-3 grid grid-cols-3 gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Alertas Imediatos</div>
                        <table className="w-full">
                          <MetricRow label="Aprovacoes" cur={h.alertas?.aprovacoes} prev={prev?.alertas?.aprovacoes} />
                          <MetricRow label="Faturas vencidas" cur={h.alertas?.faturas_vencidas} prev={prev?.alertas?.faturas_vencidas} />
                          <MetricRow label="Tickets abertos" cur={h.alertas?.tickets_abertos} prev={prev?.alertas?.tickets_abertos} />
                          <MetricRow label="Contratos 30d" cur={h.alertas?.contratos_renovar} prev={prev?.alertas?.contratos_renovar} />
                          <MetricRow label="Assinaturas pend." cur={h.alertas?.assinaturas_pendentes} prev={prev?.alertas?.assinaturas_pendentes} />
                        </table>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Tarefas</div>
                        <table className="w-full">
                          <MetricRow label="Atrasadas" cur={h.tarefas?.atrasadas} prev={prev?.tarefas?.atrasadas} />
                          <MetricRow label="Para hoje" cur={h.tarefas?.para_hoje} prev={prev?.tarefas?.para_hoje} />
                          <MetricRow label="Outras abertas" cur={h.tarefas?.outras_abertas} prev={prev?.tarefas?.outras_abertas} />
                        </table>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2 mt-3">Agendamentos</div>
                        <table className="w-full">
                          <MetricRow label="Hoje" cur={h.agendamentos?.hoje} prev={prev?.agendamentos?.hoje} />
                          <MetricRow label="Proximos 7d" cur={h.agendamentos?.proximos_7d} prev={prev?.agendamentos?.proximos_7d} />
                        </table>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Pipeline</div>
                        <table className="w-full">
                          <MetricRow label="Leads novas 7d" cur={h.pipeline?.leads_novas_7d} prev={prev?.pipeline?.leads_novas_7d} />
                          <MetricRow label="Oportunidades ativas" cur={h.pipeline?.oportunidades_ativas} prev={prev?.pipeline?.oportunidades_ativas} />
                          <MetricRow label="Propostas pend." cur={h.pipeline?.propostas_pendentes} prev={prev?.pipeline?.propostas_pendentes} />
                        </table>
                        {h.tarefas?.por_utilizador && Object.keys(h.tarefas.por_utilizador).length > 0 && (
                          <>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 mt-3">Atrasadas por utilizador</div>
                            {Object.entries(h.tarefas.por_utilizador).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([u, n]) => (
                              <div key={u} className="flex justify-between text-[11px] text-slate-600 border-b border-tint/60 py-0.5">
                                <span className="truncate pr-2">{u}</span>
                                <span className="font-bold text-red-600 shrink-0">{n}</span>
                              </div>
                            ))}
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}
